var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { localStorage } from "@play-co/browser-storage";
import { PostbackManager, PostbackManagerEvents } from "@play-co/postback-manager";
import * as userAgent from "@play-co/user-agent";
import { ABTests } from "./ABTests.mjs";
import { internalAnalytics, getAdjustedNow } from "./analytics/index.mjs";
import { trackCDNPerformance } from "./analytics/CDNPerformance.mjs";
import { isAdult, ageRangeToString, calculateNewDemographics } from "./analytics/demographics.mjs";
import { trackPlatformInitSuccess, trackGetAllFriendsSuccess, trackGetAllFriendsFailed, trackEntryFailed, trackRedirectToLineOfficialAccount, trackRedirectToLineOfficialAccountFailure, trackGetEntryTimestampsFailure, trackFirstButtonTap, trackMarketingPostbackFailure, trackSendRawAnalyticsEventFailure, trackPayloadReadFailure } from "./analytics/events.mjs";
import { getExtendedPurchaseEventProperties } from "./analytics/revenue.mjs";
import { attachFirstTapListener, isSocialChannel, getLastEntryIsRepeat, getNextGenerationProperties, getReferralChainLengths, getSessionMaturityRange } from "./analytics/utils.mjs";
import { deduplicate } from "./arrayUtils.mjs";
import { createResolvablePromise, withTiming, retryWithTiming } from "./asyncUtils.mjs";
import { removeQueryParameters } from "./browserUtils.mjs";
import { getIsoCodeFromCountryName, getCountryNameFromIsoCode } from "./countryCodes.mjs";
import { Crossplay } from "./Crossplay.mjs";
import { getConvertCurrency } from "./currencyUtils.mjs";
import { duration, getAnalyticsTimeProperties, getTimezoneName, getCalendarDaysBetween } from "./dateUtils.mjs";
import { createDom } from "./domUtils.mjs";
import { createError, unwrapNestedError } from "./errorUtils.mjs";
import { ExternalABTests } from "./ExternalABTests.mjs";
import { Friend } from "./Friend.mjs";
import getQueryParameters from "./getQueryParameters.mjs";
import globalTime from "./globalTime.mjs";
import { Pinch } from "./Pinch.mjs";
import PlatformStorage from "./PlatformStorage.mjs";
import { getTemporalUserPropertiesFromEntryTimestamps } from "./replicantExtensions/getTemporalUserProperties.mjs";
import { SignedPlayerInfo } from "./SignedPlayerInfo.mjs";
import { TermsOfService } from "./TermsOfService.mjs";
import { ToSPopup } from "./ui/ToSPopup.mjs";
const LS_KEY = "devkit-fbinstant:MockFBInstant";
const PLAYER_DATA_PREFIX = "playerData";
let sessionSequence = 0;
const featureSessionSequences = {};
class VideoAdMock {
  constructor(placementID) {
    __publicField(this, "_placementID");
    __publicField(this, "_loaded");
    __publicField(this, "_viewed");
    this._placementID = placementID;
    this._loaded = false;
    this._viewed = false;
  }
  getPlacementID() {
    return this._placementID;
  }
  loadAsync() {
    if (this._viewed)
      return Promise.reject(new Error());
    this._loaded = true;
    return Promise.resolve();
  }
  showAsync() {
    if (!this._loaded || this._viewed)
      return Promise.reject(new Error());
    this._viewed = true;
    return Promise.resolve();
  }
}
function setLocalStorageData(key, value) {
  try {
    if (!value) {
      localStorage.removeItem(LS_KEY + ":" + key);
    } else {
      localStorage.setItem(LS_KEY + ":" + key, JSON.stringify(value));
    }
  } catch (e) {
    console.error("FBINSTANT: mock setDataAsync FAILED!", e);
  }
}
function getLocalStorageData(key) {
  const value = localStorage.getItem(LS_KEY + ":" + key);
  return value ? JSON.parse(value) : void 0;
}
function getOrSetLocalStorageData(key, defaultValue) {
  try {
    const value = localStorage.getItem(LS_KEY + ":" + key);
    if (value) {
      return JSON.parse(value);
    }
    setLocalStorageData(key, defaultValue);
  } catch (e) {
    console.error("FBINSTANT: mock getDataAsync FAILED!", e);
  }
  return defaultValue;
}
const dayInMs = 1e3 * 60 * 60 * 24;
const GAME_CONFIG_DEFAULTS = {
  defaultProfilePictureURL: "https://ddw8nafke4m1l.cloudfront.net/profile-picture-fallback.png",
  linePhase: "release",
  lineCORSProxyURL: "https://line-relay-1.ap-northeast-1.dev.gc-internal.net",
  isDevelopment: false
};
class Platform {
  constructor(platformID) {
    /**
     * A/B tests manager, defined and ready to use after loading platform storage.
     *
     * @example
     * ```ts
     * await GCInstant.loadStorage();
     * GCInstant.abTests.initialize(config, generateHash);
     * const bucketId = GCInstant.abTests.getBucketID('testId');
     * ```
     *
     * @deprecated Use [Replicant A/B tests](https://docs.dev.gc-internal.net/replicant/ab-tests/) instead.
     */
    __publicField(this, "abTests");
    /**
     * Methods for loading and displaying in-game ads.
     *
     * @see https://docs.dev.gc-internal.net/gcinstant/ads/
     *
     * Only implemented on the Facebook and LINE platforms.
     */
    __publicField(this, "ads", {
      preloadInterstitialAdAsync: () => Promise.reject("Not implemented"),
      preloadRewardedInterstitialAdAsync: () => Promise.reject("Not implemented"),
      preloadRewardedVideoAdAsync: () => Promise.reject("Not implemented"),
      showPreloadedInterstitialAdAsync: () => Promise.reject("Not implemented"),
      showPreloadedRewardedInterstitialAdAsync: () => Promise.reject("Not implemented"),
      showPreloadedRewardedVideoAdAsync: () => Promise.reject("Not implemented"),
      interstitialAdIsLoaded: () => {
        throw Error("Not implemented");
      },
      rewardedInterstitialAdIsLoaded: () => {
        throw Error("Not implemented");
      },
      rewardedVideoAdIsLoaded: () => {
        throw Error("Not implemented");
      }
    });
    __publicField(this, "appID");
    /**
     * Useable after `waitForEarlyEntryData()` or `waitForEntryAnalytics()` resolves. Before that it will always be `false`.
     *
     * Only enabled on the Facebook platform.
     */
    __publicField(this, "canUseSquads", false);
    __publicField(this, "canChangeContext");
    __publicField(this, "canCreateShortcut");
    __publicField(this, "canPlayerMatch");
    __publicField(this, "_canShowInterstitialAds", false);
    __publicField(this, "_canShowRewardedInterstitialAds", false);
    __publicField(this, "_canShowRewardedVideoAds", false);
    __publicField(this, "_canRegisterForPushNotifications", false);
    /**
     * `true` if user can call `subscribeBotAsync` to subscribe to the platform chatbot.
     *
     * Useable after either `waitForEarlyEntryData()` or `waitForEntryAnalytics()` resolves. Before that the value is always `false`.
     */
    __publicField(this, "canSubscribeToBot");
    __publicField(this, "catalog");
    /**
     * Interact with official game pages and groups.
     *
     * Only enabled on the Facebook platform.
     */
    __publicField(this, "community", {
      canFollowOfficialPageAsync: () => Promise.reject("Not implemented"),
      canJoinOfficialGroupAsync: () => Promise.reject("Not implemented"),
      followOfficialPageAsync: () => Promise.reject("Not implemented"),
      joinOfficialGroupAsync: () => Promise.reject("Not implemented")
    });
    __publicField(this, "contextID");
    __publicField(this, "contextSquad", null);
    __publicField(this, "contextTournament", null);
    /**
     * Functionality related to crossplay, i.e., transition from web to native game client.
     *
     * @example
     * // 1. Track crossplay start analytics:
     * const eventProps: CrossplayEventProperties = { ... }
     * analytics.game.crossplay.trackCrossplayStart(eventProps);
     *
     * // 2. Create crossplay payload:
     * const payload = {
     *   ...GCInstant.crossplay.getCrossplayPayload(eventProps),
     *   ...otherProps,
     * }
     *
     * // 3. Open the native app:
     * const payloadQueryParam = encodeURIComponent(JSON.stringify(payload));
     * window.location.assign(`${deepLink}?payload=${payloadQueryParam}`);
     *
     * // 4. Track crossplay success analytics on entry:
     * if (GCInstant.crossplay.isCrossplayEntry()) {
     *   analytics.game.crossplay.trackCrossplayFinish(GCInstant.entryData.crossplayTracking);
     * }
     */
    __publicField(this, "crossplay", new Crossplay(this));
    __publicField(this, "contextType");
    __publicField(this, "entryAnalyticsBundle");
    __publicField(this, "entryContextTournament", null);
    /**
     * Analytics payload attached to the entry point (ad, chatbot message, etc) used to start the current session.
     *
     * The payload is constructed by the entry point creator, i.e., the referrer.
     * The payload **does not** include the current user's analytics user properties.
     *
     * Ready to use after resolving `startGameAsync`.
     *
     * @see https://docs.dev.gc-internal.net/gcinstant/#data-flow
     */
    __publicField(this, "entryData", null);
    __publicField(this, "entryPointName");
    /** IDs of playing platform friends. See `Platform.friends` for usage. */
    __publicField(this, "friendIds", []);
    /**
     * Platform friends who play the game.
     *
     * On the Facebook platform this is limited to friends who played within the last 90 days.
     * See `Platform.socialFriends` for accessing non-playing friends on the LINE platform.
     *
     * The value does not change during a session.
     *
     * @example Accessing friends
     * ```ts
     * await GCInstant.initializeAsync(config);
     * await GCInstant.waitForFriends(); // block until friends are resolved
     * GCInstant.friends;
     * ```
     */
    __publicField(this, "friends");
    /** Playing platforms friends mapped by ID. See `Platform.friends` for usage. */
    __publicField(this, "friendsMap");
    __publicField(this, "insideNativeIOS");
    __publicField(this, "isMobile");
    /**
     * `true` if user is subscribed to the platform chatbot.
     *
     * Useable after `startGameAsync` resolves. Before that the value is always `false`.
     */
    __publicField(this, "isSubscribedToBot", false);
    /**
     * The legacy interface for accessing native platform APIs. This should be used _instead of_ calling native-builder APIs directly.
     *
     * If this is defined, calls to this API will use the new native bridge if available, or fall back to the legacy native-builder APIs. If this is
     * undefined, then neither APIs are available.
     */
    __publicField(this, "legacyNative");
    __publicField(this, "locale");
    __publicField(this, "nativeBridge");
    __publicField(this, "osType");
    __publicField(this, "paymentsAvailable");
    /**
     * All platform friends including non-playing ones. Only populated on the LINE platform.
     *
     * Use `Friend.isPlayer` to distinguish between playing and non-playing friends.
     */
    __publicField(this, "socialFriends", []);
    /**
     * Methods for handling Terms of Service updates manually.
     *
     * By default GCInstant displays a ToS update popup automatically when needed.
     * Use these methods **only if handling ToS updates manually** in game code (see `GameConfig.disableAutomaticTosPopup`).
     *
     * Accessible after resolving `initializeAsync`.
     */
    __publicField(this, "tos");
    __publicField(this, "platformLocale");
    __publicField(this, "playerID");
    /**
     * Player's profile name, accessible after `initializeAsync`.
     *
     * On the Telegram platform this is the player's first name, not username. Access username via `Platform.username`.
     */
    __publicField(this, "playerName");
    /**
     * Player's email address, accessible after `initializeAsync`.
     *
     * Only defined on the Zoom platform.
     */
    __publicField(this, "playerEmail");
    /**
     * URL to the player's public profile picture.
     *
     * A default picture URL is used in case of users without a profile picture.
     *
     * On other platforms (FB, Telegram) `playerPhoto` will be an empty string if the player does not
     * have a profile photo.
     *
     * On the LINE platform this points to the original uploaded picture which can vary in size.
     * Use `playerPhotoSmall` and `playerPhotoLarge` instead for fixed image sizes.
     *
     * On the Telegram platform, this is only accessible after start `startGameAsync` and if Replicant
     * extensions are enabled. Player photos on Telegram are cached for 8 hours.
     */
    __publicField(this, "playerPhoto");
    /**
     * URL to the player's public profile picture, resized to 200x200px.
     *
     * `undefined` if the player has not set a profile picture.
     *
     * Defined on the following platforms:
     * - LINE: defined if user has set a non-default profile picture.
     * - web: defined if using LINE authentication, user has set a non-default profile picture and `webSkipLineUserInfoFetch` is not enabled.
     */
    __publicField(this, "playerPhotoLarge");
    /**
     * URL to the player's public profile picture, resized to 51x51px.
     *
     * `undefined` if the player has not set a profile picture.
     *
     * Defined on the following platforms:
     * - LINE: defined if user has set a non-default profile picture.
     * - web: defined if using LINE authentication, user has set a non-default profile picture and `webSkipLineUserInfoFetch` is not enabled.
     */
    __publicField(this, "playerPhotoSmall");
    __publicField(this, "players");
    __publicField(this, "playerSignature");
    __publicField(this, "regionCode");
    /**
     * Stateful analytics properties that are persisted across sessions.
     *
     * Meant for internal usage, use with caution!
     */
    __publicField(this, "storage");
    __publicField(this, "stsid");
    __publicField(this, "supportedAPIs");
    /**
     * Player's username, accessible after resolving `initializeAsync`.
     *
     * Only defined on the Telegram platform. Use `Platform.playerName` to access player name on the other platforms.
     */
    __publicField(this, "username");
    __publicField(this, "beforeContextSwitchCallbacks", []);
    __publicField(this, "afterContextSwitchCallbacks", []);
    __publicField(this, "beforeEntryFinalCallbacks", []);
    __publicField(this, "beforeStartGameAsyncCallbacks", []);
    __publicField(this, "didCallStartGameAsync", false);
    __publicField(this, "didCallSendEntryFinalAnalytics", false);
    __publicField(this, "didTrackFirstButtonTap", false);
    __publicField(this, "countryName");
    __publicField(this, "eCPM");
    __publicField(this, "externalFriendsPromise");
    __publicField(this, "friendAgeGroupDistribution");
    __publicField(this, "lastReceivedChatbotMessagePayload");
    __publicField(this, "externalABTests", new ExternalABTests({
      getInternalABTag: () => {
        var _a;
        return ((_a = this.abTests) == null ? void 0 : _a.config) ? this.abTests.getAbTag() : "";
      },
      getInternalABTestIDs: () => {
        var _a, _b;
        return ((_a = this.abTests) == null ? void 0 : _a.config) ? Object.keys((_b = this.abTests) == null ? void 0 : _b.config) : [];
      }
    }));
    __publicField(this, "fallbackPayloadGetter");
    __publicField(this, "pinch");
    __publicField(this, "entryPromise");
    /** Operations to retry after game start in case client didn't support calling the API early */
    __publicField(this, "onGameStartRetries", []);
    __publicField(this, "friendsAvailablePromise", createResolvablePromise());
    __publicField(this, "resolveFriendsAvailable", this.friendsAvailablePromise.resolve);
    __publicField(this, "rejectFriendsAvailable", this.friendsAvailablePromise.reject);
    /** Entry sequence async API call timings, in seconds since window load. */
    __publicField(this, "timeTo", {});
    /** Set to true if platform data APIs are accessible right after `initializeAsync` */
    __publicField(this, "wasEntryDataReadyEarly", false);
    /** In PlatformFacebook, set to fallback if entry event payload comes from tournament entry context payload due to no entry data available.  Otherwise, we'll mark it as direct */
    __publicField(this, "tournamentPayloadMode");
    __publicField(this, "entryTimestampsGetter");
    __publicField(this, "previousEntryTimestamp", null);
    __publicField(this, "_entryAnalyticsPromise");
    __publicField(this, "_resolveEntryAnalytics");
    __publicField(this, "_entryDataPromise");
    __publicField(this, "_gameConfig");
    __publicField(this, "_gameStarted");
    __publicField(this, "_initialization");
    __publicField(this, "_initialized");
    __publicField(this, "_instance");
    __publicField(this, "_isPaymentsReady");
    __publicField(this, "_loadStoragePromise");
    __publicField(this, "_matchingEnabled");
    __publicField(this, "_onPause");
    __publicField(this, "_onResume");
    __publicField(this, "_onPaymentsReady");
    __publicField(this, "_onProvisionProductAsync");
    __publicField(this, "_resolveEntryData");
    __publicField(this, "_sessionStartTime");
    __publicField(this, "_supportedAPIs");
    __publicField(this, "dataCodec", {
      encode: (data) => data,
      decode: (data) => Promise.resolve(data)
    });
    __publicField(this, "onVisibilityChange");
    __publicField(this, "originalLocationHref");
    __publicField(this, "convertCurrency", () => {
      return void 0;
    });
    this.platformID = platformID;
    this.playerID = "";
    this.playerName = "";
    this.playerPhoto = "";
    this.playerSignature = "";
    this.friends = [];
    this.friendsMap = {};
    this.players = [];
    this.catalog = [];
    this.contextID = null;
    this.contextType = "SOLO";
    this.platformLocale = "en";
    this.locale = "en";
    this.regionCode = null;
    this.isMobile = false;
    this.paymentsAvailable = false;
    this.insideNativeIOS = false;
    this.canSubscribeToBot = false;
    this._initialization = null;
    this._initialized = false;
    this._gameStarted = false;
    this._onPause = null;
    this._onResume = null;
    this._onPaymentsReady = null;
    this._isPaymentsReady = false;
    this._onProvisionProductAsync = null;
    this._matchingEnabled = false;
    this._supportedAPIs = [];
    this._sessionStartTime = Date.now();
    this.canChangeContext = false;
    this.canCreateShortcut = false;
    this.canPlayerMatch = false;
    this.supportedAPIs = {};
    this._gameConfig = null;
    this._resolveEntryData;
    this._entryDataPromise = new Promise((resolve, reject) => {
      this._resolveEntryData = { resolve, reject };
    });
    this.storage = new PlatformStorage(this);
    this.pinch = new Pinch(this.storage);
    this.tos = new TermsOfService(this);
    this.stsid = Date.now();
    this._entryAnalyticsPromise = new Promise((resolve, reject) => {
      this._resolveEntryAnalytics = { resolve, reject };
    });
  }
  /**
   * True if platform can show interstitial ads.
   *
   * @throws If called before `initializeAsync` resolves.
   */
  get canShowInterstitialAds() {
    this.throwIfNotInitialized();
    return this._canShowInterstitialAds;
  }
  /**
   * True if platform can show rewarded interstitial ads.
   *
   * @throws If called before `initializeAsync` resolves.
   */
  get canShowRewardedInterstitialAds() {
    this.throwIfNotInitialized();
    return this._canShowRewardedInterstitialAds;
  }
  /**
   * True if platform can show rewarded video ads.
   *
   * @throws If called before `initializeAsync` resolves.
   */
  get canShowRewardedVideoAds() {
    this.throwIfNotInitialized();
    return this._canShowRewardedVideoAds;
  }
  /**
   * True if the platform can register for push notifications.
   *
   * @throws If called before `initializeAsync` resolves.
   */
  get canRegisterForPushNotifications() {
    this.throwIfNotInitialized();
    return this._canRegisterForPushNotifications;
  }
  /**
   * Resolves after early platform entry sequence finishes, or immediately in
   * case early platform APIs are not available.
   */
  waitForEarlyEntryData() {
    this.throwIfNotInitialized();
    return this.entryPromise ?? Promise.resolve();
  }
  /**
   * Resolves after initial platform friends load finishes and `friends` is populated.
   *
   *  @throws if platform friends load fails or the external friends promise set with `setExternalFriendsPromise` rejects.
   */
  waitForFriends() {
    if (this._gameConfig.disablePlatformFriends && !this.externalFriendsPromise) {
      return Promise.resolve();
    }
    return this.friendsAvailablePromise.promise;
  }
  /**
   * Get user's estimated demographic information.
   *
   * The return value can differ based on when the method is called during the session:
   * - Called before resolving `sendEntryFinalAnalytics`: the return value **does not** account for any demographics in the current entry payload.
   * - Called after resolving `sendEntryFinalAnalytics` or in the `beforeEntryFinal` callback: the return value **does** account for the current entry payload.
   *
   * Only available on the LINE platform.
   *
   * @throws if called before loading analytics storage, i.e., before resolving `Platform.waitForEntryAnalytics()`.
   */
  getDemographics() {
    this.throwIfStorageIsNotLoaded();
    const demographics = JSON.parse(
      JSON.stringify(this.storage.demographics)
    );
    if (demographics.ageRange) {
      demographics.isAdult = isAdult(demographics.ageRange);
    }
    return demographics;
  }
  readConfiguration(config) {
    var _a, _b, _c, _d;
    if (config.appID && typeof config.appID === "string") {
      this.appID = config.appID;
    }
    this._gameConfig = { ...GAME_CONFIG_DEFAULTS, ...config };
    try {
      if (process.env.APP_ID || process.env.FB_APP_ID) {
        this.appID = process.env.APP_ID || process.env.FB_APP_ID;
      }
      if (process.env.AMPLITUDE) {
        this._gameConfig.amplitudeKey = process.env.AMPLITUDE;
      }
      if (process.env.AMPLITUDE_AD_SERVER) {
        this._gameConfig.amplitudeAdServerKey = process.env.AMPLITUDE_AD_SERVER;
      }
      if (process.env.AMPLITUDE_TIME_ZONE) {
        this._gameConfig.amplitudeTimeZone = process.env.AMPLITUDE_TIME_ZONE;
      }
      if (process.env.DEFAULT_PROFILE_PICTURE_URL) {
        this._gameConfig.defaultProfilePictureURL = process.env.DEFAULT_PROFILE_PICTURE_URL;
      }
      if (process.env.DISABLE_AUTOMATIC_TOS_POPUP === "true" || typeof process.env.DISABLE_AUTOMATIC_TOS_POPUP === "boolean" && process.env.DISABLE_AUTOMATIC_TOS_POPUP === true) {
        this._gameConfig.disableAutomaticTosPopup = true;
      }
      if (process.env.DISABLE_PLATFORM_FRIENDS === "true" || typeof process.env.DISABLE_PLATFORM_FRIENDS === "boolean" && process.env.DISABLE_PLATFORM_FRIENDS === true) {
        this._gameConfig.disablePlatformFriends = true;
      }
      if (process.env.DISABLE_WEB_PUSH === "true" || typeof process.env.DISABLE_WEB_PUSH === "boolean" && process.env.DISABLE_WEB_PUSH === true) {
        this._gameConfig.disableWebPush = true;
      }
      if (process.env.FB_PIXEL_ID) {
        this._gameConfig.fbPixelId = process.env.FB_PIXEL_ID;
      }
      if (process.env.FB_INSTANT_MOCK_SIGNATURE) {
        this._gameConfig.mockSignature = process.env.FB_INSTANT_MOCK_SIGNATURE;
      }
      if (process.env.GOOGLE_TAG_MANAGER_ID) {
        this._gameConfig.googleTagManagerId = process.env.GOOGLE_TAG_MANAGER_ID;
      }
      if (process.env.IS_DEVELOPMENT === "true" || typeof process.env.IS_DEVELOPMENT === "boolean" && process.env.IS_DEVELOPMENT === true) {
        this._gameConfig.isDevelopment = true;
      }
      if (process.env.LINE_PHASE) {
        this._gameConfig.linePhase = {
          sandbox: "sandbox",
          staging: "staging",
          release: "release"
        }[process.env.LINE_PHASE.toLowerCase()];
        if (!this._gameConfig.linePhase) {
          throw new Error(
            "Unexpected value for LINE_PHASE: " + JSON.stringify(process.env.LINE_PHASE)
          );
        }
      }
      if (process.env.LINE_CORS_PROXY_URL) {
        this._gameConfig.lineCORSProxyURL = process.env.LINE_CORS_PROXY_URL;
      }
      if (process.env.LINE_LIFF_ID) {
        this._gameConfig.lineLiffID = process.env.LINE_LIFF_ID;
      }
      if (process.env.LINE_OA_ID) {
        this._gameConfig.lineOfficialAccountID = process.env.LINE_OA_ID;
      }
      if (process.env.LINE_TAG_ID || process.env.LINE_TAG_ACCESS_TOKEN || process.env.LINE_CHANNEL_ID || process.env.LINE_CONVERSION_API_ENDPOINT) {
        this._gameConfig.lineConversionAPIConfig = {
          channelID: process.env.LINE_CHANNEL_ID ?? ((_a = this._gameConfig.lineConversionAPIConfig) == null ? void 0 : _a.channelID),
          conversionAPIEndpoint: process.env.LINE_CONVERSION_API_ENDPOINT ?? ((_b = this._gameConfig.lineConversionAPIConfig) == null ? void 0 : _b.conversionAPIEndpoint),
          tagAccessToken: process.env.LINE_TAG_ACCESS_TOKEN ?? ((_c = this._gameConfig.lineConversionAPIConfig) == null ? void 0 : _c.tagAccessToken),
          tagID: process.env.LINE_TAG_ID ?? ((_d = this._gameConfig.lineConversionAPIConfig) == null ? void 0 : _d.tagID)
        };
      }
      if (process.env.OAUTH_AUTHORIZATION_CODE_URL) {
        this._gameConfig.oauthAuthorizationCodeUrl = process.env.OAUTH_AUTHORIZATION_CODE_URL;
      }
      if (process.env.POSTBACK_MANAGER_KEY) {
        this._gameConfig.postbackManagerKey = process.env.POSTBACK_MANAGER_KEY;
      }
      if (process.env.REDIRECT_TO_ROOT_ON_AUTH_FAILURE === "true" || typeof process.env.REDIRECT_TO_ROOT_ON_AUTH_FAILURE === "boolean" && process.env.REDIRECT_TO_ROOT_ON_AUTH_FAILURE === true) {
        this._gameConfig.redirectToRootOnAuthFailure = true;
      }
      if (process.env.REVENUE_CURRENCY) {
        this._gameConfig.revenueCurrency = process.env.REVENUE_CURRENCY;
      }
      if (process.env.STRIPE_PUBLISHABLE_KEY) {
        this._gameConfig.stripePublishableKey = process.env.STRIPE_PUBLISHABLE_KEY;
      }
      if (process.env.TELEGRAM_BOT_USERNAME) {
        this._gameConfig.telegramBotUsername = process.env.TELEGRAM_BOT_USERNAME;
      }
      if (process.env.TUNE_POSTBACK_ENDPOINT) {
        this._gameConfig.tunePostbackEndpoint = process.env.TUNE_POSTBACK_ENDPOINT;
      }
      if (process.env.USE_LEGACY_TOS_LINKS === "true" || typeof process.env.USE_LEGACY_TOS_LINKS === "boolean" && process.env.USE_LEGACY_TOS_LINKS === true) {
        this._gameConfig.useLegacyToSLinks = true;
      }
      if (process.env.WEB_PUSH_PUBLIC_KEY) {
        this._gameConfig.webPushPublicKey = process.env.WEB_PUSH_PUBLIC_KEY;
      }
      if (process.env.WEB_SKIP_LINE_PROFILE_FETCH === "true" || typeof process.env.WEB_SKIP_LINE_PROFILE_FETCH === "boolean" && process.env.WEB_SKIP_LINE_PROFILE_FETCH === true) {
        this._gameConfig.webSkipLineUserInfoFetch = true;
      }
      if (process.env.ZOOM_API_ENDPOINT) {
        this._gameConfig.zoomAPIEndpoint = process.env.ZOOM_API_ENDPOINT;
      }
      if (process.env.ZOOM_SDK_CAPABILITIES) {
        if (!this._gameConfig.zoomSDKConfig) {
          this._gameConfig.zoomSDKConfig = { capabilities: [] };
        }
        this._gameConfig.zoomSDKConfig.capabilities = process.env.ZOOM_SDK_CAPABILITIES.split(",");
      }
      if (process.env.ZOOM_MEETING_CLIENT_USER_PROPS) {
        this._gameConfig.zoomMeetingClientUserProperties = process.env.ZOOM_MEETING_CLIENT_USER_PROPS.split(",");
      }
      if (process.env.ZOOM_MAIN_CLIENT_USER_PROPS) {
        this._gameConfig.zoomMainClientUserProperties = process.env.ZOOM_MAIN_CLIENT_USER_PROPS.split(",");
      }
    } catch {
    }
    if (!this.appID) {
      throw Error("appID is not defined");
    }
    if (this._gameConfig.amplitudeKey && !this._gameConfig.amplitudeTimeZone) {
      throw createError(
        "INVALID_PARAM",
        "amplitudeTimeZone is undefined: define the value either in the initializeAsync call or with the AMPLITUDE_TIME_ZONE environment variable"
      );
    }
    if (!this._gameConfig.revenueCurrency) {
      throw createError(
        "INVALID_PARAM",
        "revenueCurrency is undefined: define the value either in the initializeAsync call or with the REVENUE_CURRENCY environment variable"
      );
    }
    try {
      (/* @__PURE__ */ new Date()).toLocaleString("en-US", {
        timeZone: this._gameConfig.amplitudeTimeZone
      });
    } catch (error) {
      if (this._gameConfig.isDevelopment) {
        console.warn(
          `Invalid Amplitude time zone in GCInstant.initializeAsync: ${this._gameConfig.amplitudeTimeZone}. Defaulting to UTC`
        );
      }
      this._gameConfig.amplitudeTimeZone = "UTC";
    }
    if (this._gameConfig.isDevelopment) {
      console.warn(
        "Running GCInstant in development mode - make sure to disable development mode in production."
      );
    }
  }
  _initialize(playerID) {
    if (!playerID) {
      throw new Error("playerID is falsey!");
    }
    if (!this._gameConfig) {
      throw Error(
        "Game configuration is not defined: call readConfiguration before initializing"
      );
    }
    this.playerID = playerID;
    this._initialized = true;
    this.getEntryPointData();
    this.setLoadingProgress(0);
    internalAnalytics.setUserID(playerID);
    internalAnalytics.trackSessionStartEvents();
    trackPlatformInitSuccess();
    this.originalLocationHref = location.href;
    return this.refreshPlayerSignature().then(() => {
      this.trackTimeTo("getSignedPlayerInfoAsync");
    });
  }
  /**
   * Enables payload encoding.
   * More info in [Payload Encoding](https://docs.dev.gc-internal.net/gcinstant/replicant-extensions/#payload-encoding).
   *
   * @throws when called after `startGameAsync`.
   *
   * @param dataCodec encode and decode functions for data sent to platform functions and for received payloads.
   */
  setDataCodec(dataCodec) {
    if (this.didCallStartGameAsync) {
      throw new Error(
        `Set an encoder and decoder before calling 'startGameAsync'. Otherwise, some encoded payloads may not be decoded correctly.`
      );
    }
    this.dataCodec = dataCodec;
  }
  /**
   * Pass in promise of an external friends list to use instead of platform friend APIs.
   *
   * When external friends are defined, GCInstant skips fetching platform friends on initialization
   * and uses external friends instead to populate properties like `GCInstant.friends`.
   *
   * If using timestep-build's load time optimizations, the `DISABLE_PLATFORM_FRIENDS` environment variable must also be set to prevent early loading of platform friends.
   *
   * Only enabled on the LINE and web platforms.
   *
   * @throws `INVALID_OPERATION` if called after `initializeAsync` unless `disablePlatformFriends` is enabled.
   */
  setExternalFriendsGetter(getExternalFriends) {
    if (this._initialization && !this._gameConfig.disablePlatformFriends) {
      throw createError(
        "INVALID_OPERATION",
        "External friends must be set before initializeAsync"
      );
    }
    this.externalFriendsPromise = getExternalFriends().then((externalFriends) => {
      this.updateExternalFriends(externalFriends);
      trackGetAllFriendsSuccess({
        friendCount: this.socialFriends.length
      });
      this.resolveFriendsAvailable();
    }).catch((error) => {
      trackGetAllFriendsFailed(error);
      this.rejectFriendsAvailable(error);
    });
  }
  /**
   * Update the external friends list used instead of platform friend APIs.
   *
   * Use `setExternalFriendsGetter` to set the initial external friends on game start.
   *
   * Only enabled on the LINE and web platforms.
   */
  updateExternalFriends(newExternalFriends) {
    const socialFriends = newExternalFriends.map(
      (externalFriend) => Friend.fromExternalFriend(externalFriend)
    );
    this.friends = socialFriends.filter((friend) => friend.isPlayer);
    this.socialFriends = socialFriends;
    this.friendIds = this.friends.map((friend) => friend.id);
    this.friendsMap = this.friends.reduce(
      (acc, friend) => {
        acc[friend.id] = friend;
        return acc;
      },
      {}
    );
  }
  /** Set a payload fetcher function that is used to populate `Platform.entryData` in case of no payload on entry. */
  setFallbackPayloadGetter(getter) {
    this.fallbackPayloadGetter = async () => {
      try {
        return await getter() || {};
      } catch {
        return {};
      }
    };
  }
  /**
   * Set friends analytics data, typically fetched with the `getFriendAnalytics`
   * async getter from `@play-co/gcinstant/replicantExtensions`.
   *
   * Call once per session before `sendEntryFinalAnalytics`.
   */
  setFriendAnalytics(friendAnalytics) {
    this.throwIfNotInitialized();
    this.friendAgeGroupDistribution = friendAnalytics.ageGroupDistribution;
    const cluster = friendAnalytics.clusterCounts;
    const friend = friendAnalytics.activeFriendCounts;
    function getRatio(numerator, denominator) {
      const ratio = numerator / denominator;
      return Number.isFinite(ratio) ? ratio : void 0;
    }
    internalAnalytics.setUserProperties({
      lastEntryActiveClusterSize1D: cluster.clusterSize1D,
      lastEntryActiveClusterSize3D: cluster.clusterSize3D,
      lastEntryActiveClusterSize7D: cluster.clusterSize7D,
      lastEntryActiveClusterSize14D: cluster.clusterSize14D,
      lastEntryActiveClusterSize30D: cluster.clusterSize30D,
      lastEntryActiveClusterSize90D: cluster.clusterSize90D,
      lastEntryActiveClusterConnections1D: cluster.clusterConnections1D,
      lastEntryActiveClusterConnections3D: cluster.clusterConnections3D,
      lastEntryActiveClusterConnections7D: cluster.clusterConnections7D,
      lastEntryActiveClusterConnections14D: cluster.clusterConnections14D,
      lastEntryActiveClusterConnections30D: cluster.clusterConnections30D,
      lastEntryActiveClusterConnections90D: cluster.clusterConnections90D,
      lastEntryActiveClusterConnectedness1D: cluster.clusterConnectedness1D,
      lastEntryActiveClusterConnectedness3D: cluster.clusterConnectedness3D,
      lastEntryActiveClusterConnectedness7D: cluster.clusterConnectedness7D,
      lastEntryActiveClusterConnectedness14D: cluster.clusterConnectedness14D,
      lastEntryActiveClusterConnectedness30D: cluster.clusterConnectedness30D,
      lastEntryActiveClusterConnectedness90D: cluster.clusterConnectedness90D,
      lastEntryActiveFriendCount1D: friend.activeFriendCount1D,
      lastEntryActiveFriendCount3D: friend.activeFriendCount3D,
      lastEntryActiveFriendCount7D: friend.activeFriendCount7D,
      lastEntryActiveFriendCount14D: friend.activeFriendCount14D,
      lastEntryActiveFriendCount30D: friend.activeFriendCount30D,
      lastEntryActiveFriendCount90D: friend.activeFriendCount90D,
      "lastEntryActiveFriendRatio1/3": getRatio(
        friend.activeFriendCount1D,
        friend.activeFriendCount3D
      ),
      "lastEntryActiveFriendRatio1/7": getRatio(
        friend.activeFriendCount1D,
        friend.activeFriendCount7D
      ),
      "lastEntryActiveFriendRatio3/7": getRatio(
        friend.activeFriendCount3D,
        friend.activeFriendCount7D
      ),
      "lastEntryActiveFriendRatio7/30": getRatio(
        friend.activeFriendCount7D,
        friend.activeFriendCount30D
      )
    });
  }
  /**
   * Pass in the analytics payload of the user's last received platform chatbot message.
   *
   * Used to populate the `latestChatbot*` event properties in `EntryFinal`.
   *
   * @throws `INVALID_OPERATION` if called after `startGameAsync`.
   */
  setLastReceivedChatbotMessagePayload(payload) {
    if (this._gameStarted) {
      throw createError(
        "INVALID_OPERATION",
        "Method must be called before `startGameAsync`"
      );
    }
    this.lastReceivedChatbotMessagePayload = payload;
  }
  async showToSPopupToExistingUsers() {
    this.throwIfNotInitialized();
    const tosVersion = "2022-12";
    if (await this.tos.hasAcceptedToS(tosVersion)) {
      return;
    }
    this.tos.trackPopupOpenEvent(tosVersion);
    const popup = createDom(ToSPopup.ELEMENT_NAME);
    popup.locale = this.locale;
    popup.useLegacyLinks = !!this._gameConfig.useLegacyToSLinks;
    const { requestMs } = await withTiming(
      () => new Promise((resolve) => {
        popup.onClose(() => resolve());
      })
    );
    this.tos.acceptToS(tosVersion, { requestMs });
  }
  /**
   * Called after `startGameAsync` to
   * - handle the minority of clients whose entry data is not ready right
   *   after `initializeAsync`,
   * - decode entry data in case a `dataCodec` is set, and
   * - set default entry data values.
   */
  async _prepareEntryData() {
    const prepareEntryDataInternal = async () => {
      this.entryData = this._getEntryPointDataForce() || this.entryData;
      const useFallbackPayload = Object.keys(this.entryData || {}).length === 0 && this.fallbackPayloadGetter;
      if (useFallbackPayload) {
        this.entryData = await this.fallbackPayloadGetter();
      }
      const decodedEntryData = await this.dataCodec.decode(this.entryData);
      if (decodedEntryData) {
        this.entryData = decodedEntryData;
      }
    };
    return prepareEntryDataInternal().then(this._resolveEntryData.resolve).catch((err) => {
      this._resolveEntryData.reject(err);
      throw err;
    });
  }
  /**
   * Starts the game once assets are loaded and the backend is ready.
   *
   * @throws If called more than once, or if storage adapter is not configured when in production mode.
   */
  async startGameAsync() {
    this.throwIfNotInitialized();
    this.throwIfStartGameAsyncIsCalledMultipleTimes();
    this.throwIfStorageAdapterIsNotConfigured();
    try {
      await Promise.all(
        this.beforeStartGameAsyncCallbacks.map((callback) => callback())
      );
      await this.startGameAsyncInner();
      this._gameStarted = true;
      attachFirstTapListener();
      void this._sendEntryAnalytics();
      if (!this._gameConfig.disableAutomaticTosPopup) {
        void this.showToSPopupToExistingUsers();
      }
    } catch (error) {
      trackEntryFailed(unwrapNestedError(error));
      throw unwrapNestedError(error);
    }
  }
  /** Add a callback that is invoked before each context switch attempt. */
  beforeContextSwitch(callback) {
    this.beforeContextSwitchCallbacks.push(callback);
  }
  /** Add a callback that is invoked after each successful context switch. */
  afterContextSwitch(callback) {
    this.afterContextSwitchCallbacks.push(callback);
  }
  /**
   * Add a callback that is invoked before tracking `EntryFinal` and setting first and last entry user properties.
   *
   * Analytics storage properties like `entry.last` and `demographics` are updated before invoking `callback`.
   * The only exception is `storage.analytics` which is updated with first entry user properties after invoking `callback`.
   */
  beforeEntryFinal(callback) {
    this.beforeEntryFinalCallbacks.push(callback);
  }
  /** Add a callback that is invoked and resolved before `startGameAsync`. */
  beforeStartGameAsync(callback) {
    this.beforeStartGameAsyncCallbacks.push(callback);
  }
  /** Set a callback that is invoked when the game should be paused, such as when going into the background. */
  onPause(onPause) {
    this._onPause = onPause;
    this.updateVisibilityChangeListener();
  }
  /** Set a callback that is invoked when the game should be resumed, such as when going into the foreground. */
  onResume(onResume) {
    this._onResume = onResume;
    this.updateVisibilityChangeListener();
  }
  updateVisibilityChangeListener() {
    if (this._onPause || this._onResume) {
      if (!this.onVisibilityChange) {
        this.onVisibilityChange = () => {
          var _a, _b;
          if (document.hidden) {
            (_a = this._onPause) == null ? void 0 : _a.call(this);
          } else {
            (_b = this._onResume) == null ? void 0 : _b.call(this);
          }
        };
        document.addEventListener("visibilitychange", this.onVisibilityChange);
      }
    } else if (this.onVisibilityChange) {
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      this.onVisibilityChange = void 0;
    }
  }
  onPaymentsReady(onPaymentsReady) {
    if (this._isPaymentsReady) {
      onPaymentsReady();
      return;
    }
    this._onPaymentsReady = onPaymentsReady;
  }
  /**
   * In order for the devkit-instant purchase functions to work they need a
   * means of applying a purchased product to a users account. Because this
   * product and possibly persistantce layer are unique to the game itself the
   * game must provide devkit-instant with a callback that performs those game
   * specific purchase tasks and resovles when finished.
   *
   * @param onProvisionProductAsync Returns a promise that resolves
   * after the game has successfully provisioned the users account with the
   * purchased product.
   */
  onProvisionProductAsync(onProvisionProductAsync) {
    this._onProvisionProductAsync = onProvisionProductAsync;
  }
  /**
   * Helper function to retrive Product metadata from the catalog by productId.
   *
   * @param productId Id of the product to retrieve from the catalog.
   */
  _getProduct(productId) {
    if (this.catalog.length === 0)
      return void 0;
    for (const product of this.catalog) {
      if (product.id === productId) {
        return product;
      }
    }
    return void 0;
  }
  checkCanPlayerMatchAsync() {
    return Promise.resolve(false);
  }
  matchPlayerAsync(matchTag, switchContextWhenMatched, offlineMatch) {
    return Promise.reject(new Error("not implemented"));
  }
  quit() {
  }
  getSDKVersion() {
  }
  setLoadingProgress(value, bytesLoaded) {
  }
  /**
   * Behavior varies per platform:
   * - FB: calls `FBInstant.setLoadingProgress(100)`.
   * - LINE, Telegram: removes the `#splashScreen` element injected by timestep-build.
   */
  dismissLoadingScreenAsync() {
    return Promise.resolve();
  }
  getCatalogAsync() {
    return Promise.reject(new Error("not implemented"));
  }
  setSessionData(data) {
    throw new Error("not implemented");
  }
  /**
   * Initializes in app purchase API
   * TODO: move onPaymentsReady and onProvisionProductAsync as arguments here
   *
   * @param options.productIDs product id list as found on the app store for given platform OS.
   */
  async iapInit(options) {
  }
  /**
   * @throws `INVALID_PARAM` when using native app IAP if `productID` is not one of the product IDs configured with `Platform.iapInit`.
   */
  purchaseAsync(productID, payload) {
    return Promise.reject(new Error("not implemented"));
  }
  getDataAsync(keys) {
    const data = {};
    for (const key of keys) {
      data[key] = getOrSetLocalStorageData(`${PLAYER_DATA_PREFIX}:${key}`);
    }
    return Promise.resolve(data);
  }
  setDataAsync(data) {
    return Promise.resolve().then(() => {
      Object.keys(data).forEach((key) => {
        setLocalStorageData(`${PLAYER_DATA_PREFIX}:${key}`, data[key]);
      });
    });
  }
  /**
   * Note that GCInstant fetches platform friends automatically on initialization:
   * use `Platform.friends` instead of this method to avoid duplicate requests.
   *
   * @returns A promise that resolves with a list of **playing** friends.
   */
  getFriendsAsync() {
    return Promise.resolve(this.friends);
  }
  filterFriendsAsync(filters) {
    return Promise.resolve(this.friends);
  }
  getPlayersAsync() {
    return Promise.resolve(this.friends);
  }
  getContextPlayersAsync() {
    return Promise.resolve([]);
  }
  flushDataAsync() {
    return Promise.resolve();
  }
  getSignedPlayerInfoAsync(requestPayload) {
    return Promise.resolve(new SignedPlayerInfo());
  }
  /**
   * Fetch a new player signature from platform and update `this.playerSignature`.
   *
   * @returns New player signature.
   */
  async refreshPlayerSignature() {
    this.throwIfNotInitialized();
    return this.getSignedPlayerInfoAsync("LOGIN_REQUEST").then(
      (signedPlayerInfo) => {
        this.playerSignature = signedPlayerInfo.signature;
        return this.playerSignature;
      }
    );
  }
  switchGameAsync(gameId, data) {
    return Promise.reject(new Error(""));
  }
  /**
   * Check if the client can switch to its Native (Android/iOS) Game.
   *
   * @returns Whether or not client can switch to Native game.
   */
  async canSwitchNativeGameAsync() {
    return false;
  }
  /**
   * Request that the client switch to its Native (Android/iOS) Game.
   * The API will reject if the switch fails - else, the client will open the Game or Store.
   *
   * @param data This will be set as the entrypoint data for the game being switched to.
   */
  async switchNativeGameAsync(data) {
    throw new Error("GCInstant.switchNativeGameAsync: Not implemented");
  }
  canCreateShortcutAsync() {
    return Promise.resolve().then(() => this.canCreateShortcut = false);
  }
  createShortcutAsync(feature) {
    return Promise.reject(new Error(""));
  }
  /**
   * This method is called internally on initialization: use the `canSubscribeBot` attribute instead.
   */
  canSubscribeBotAsync() {
    return Promise.resolve(this.canSubscribeToBot);
  }
  /**
   * Subscribe to the platform chatbot.
   *
   * - FB: opens a UI dialog for confirming chatbot subscription.
   * - LINE: leaves the game and redirects user to the game's Official Account page in the LINE app (determined by the `lineOfficialAccountID` configuration option), similarly to `redirectToLineOfficialAccount`.
   * - web: subscribes to Web Push API notifications.
   *
   * Check `canSubscribeToBot` before calling this method.
   *
   * Only enabled on the Facebook, LINE and web platforms.
   *
   * @param subscriptionFlow An optional promise to resolve before requesting platform chatbot subscription.
   * @param analytics Extra event properties attached to the `SubscribeBotAsync*` events.
   *
   * @throws `USER_INPUT` if user dismissed the UI dialog on Facebook.
   */
  subscribeBotAsync(subscriptionFlow, analytics2) {
    return subscriptionFlow ?? Promise.resolve();
  }
  /**
   * Redirect to the [LINE Official Account page](https://developers.line.biz/en/docs/messaging-api/using-line-url-scheme/#sharing-line-official-account) configured by `lineOfficialAccountID`.
   *
   * @throws If `lineOfficialAccountID` is not configured.
   */
  redirectToLineOfficialAccount(analytics2) {
    trackRedirectToLineOfficialAccount(analytics2);
    const { lineOfficialAccountID } = this._gameConfig;
    if (!lineOfficialAccountID) {
      const error = createError(
        "INVALID_PARAM",
        "LINE Official Account ID is not configured"
      );
      trackRedirectToLineOfficialAccountFailure(error, analytics2);
      throw error;
    }
    window.location.href = "https://line.me/R/ti/p/" + lineOfficialAccountID;
  }
  /**
   * Get an interstitial ad instance.
   *
   * **Use the high-level `Platform.ads` interface** instead for basic ad use cases.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/ads/
   */
  getInterstitialAdAsync(placementID) {
    return Promise.resolve(new VideoAdMock(placementID));
  }
  /**
   * Get a rewarded interstitial ad instance.
   *
   * **Use the high-level `Platform.ads` interface** instead for basic ad use cases.
   *
   * Only enabled on the Facebook platform.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/ads/
   */
  getRewardedInterstitialAdAsync(placementID) {
    return Promise.reject(
      createError(
        "INVALID_OPERATION",
        "Operation not supported by the current platform"
      )
    );
  }
  /**
   * Get a rewarded video ad instance.
   *
   * **Use the high-level `Platform.ads` interface** instead for basic ad use cases.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/ads/
   */
  getRewardedVideoAsync(placementID) {
    return Promise.resolve(new VideoAdMock(placementID));
  }
  /**
   * For the LINK platform, this will send a message to the chosen user.
   * That message is configurable via messageOpts, otherwise a default will be used.
   */
  chooseContextAsync(opts) {
    return Promise.resolve();
  }
  switchContextAsync(contextID, analyticsData) {
    this.contextID = contextID;
    return Promise.resolve();
  }
  /**
   * @param uiOpts Options for the LINE platform confirmation UI dialog. Only enabled on the LINE platform.
   */
  createContextAsync(playerID, analyticsData, uiOpts) {
    return Promise.resolve();
  }
  isContextPublicAsync(contextID) {
    return Promise.resolve(true);
  }
  /**
   * Resolves to `true` if the current user is eligible to use squads.
   *
   * Use the `canUseSquads` property instead whenever possible.
   *
   * Available on the Facebook platforms only.
   */
  canUseSquadsAsync() {
    return Promise.reject(new Error("not implemented"));
  }
  /**
   * Create a new squad and switch to squad context.
   *
   * - FB: opens a UI dialog for inputting squad options.
   *
   * Only enabled on the Facebook platform.
   *
   * @example
   * ```ts
   * // Check if squads feature is available:
   * if (!GCInstant.canUseSquads) {
   *   return;
   * }
   *
   * // Create a squad:
   * const squad = await GCInstant.createSquadAsync(createSquadOpts);
   *
   * // Once in a squad context, send a squad message:
   * await GCInstant.updateAsync(updateOpts);
   *
   * // Join an existing squad:
   * const squad = await GCInstant.getSquadAsync(squadIdOrContextId);
   * await squad.joinSquadAsync(joinOpts);
   * ```
   *
   * @throws `USER_INPUT` if user dismissed the UI dialog on Facebook.
   * @throws If the platform SDK call fails for any other reason.
   */
  createSquadAsync(opts) {
    return Promise.reject(new Error("not implemented"));
  }
  /**
   * Retrieve a squad.
   *
   * Only enabled on the Facebook platform.
   *
   * @throws `GAMING_SQUAD_NOT_FOUND` if squad is not found.
   *
   * @param squadOrContextId Squad ID or squad context ID.
   */
  getSquadAsync(squadOrContextId) {
    return Promise.reject(new Error("not implemented"));
  }
  /**
   * Only enabled on the Facebook platform.
   *
   * @returns a promise that resolves with an array of the gaming squads the current player is in.
   */
  getPlayerSquadsAsync() {
    return Promise.reject(new Error("not implemented"));
  }
  /**
   * Returns a Tournament object or rejects if no tournament was found in current context.
   */
  async getTournamentAsync() {
    return Promise.reject(new Error("not implemented"));
  }
  /**
   * Opens the tournament creation dialog if the player is not currently in a tournament session.
   *
   * @returns tournament object if creation success & switched into social context of the Tournament; otherwise the promise is rejected with error.
   */
  createTournamentAsync(opts) {
    return Promise.reject("GCInstant.createTournamentAsync: Not implemented");
  }
  /**
   * Must be in context of that Tournament.
   *
   * @returns Promise is resolved if the tournament is shared; otherwise the promise is rejected with error.
   */
  shareTournamentAsync(opts) {
    return Promise.reject("GCInstant.shareTournamentAsync: Not implemented");
  }
  /**
   * Updates a user's score in a tournament and does not prompt the tournament creation/share dialog.
   *
   * @param score The user's current score in the tournament
   * @param analyticsData Any custom analytics the game wants to track.
   *
   * @returns The promise is resolved to void if post score success; otherwise the promise is rejected with error.
   */
  postTournamentScoreAsync(score, analyticsData) {
    return Promise.reject(
      "GCInstant.postTournamentScoreAsync: Not implemented"
    );
  }
  /**
   * Set a getter function for fetching other players' entry timestamps.
   * Entry timestamps are fetched when posting events like `EntryConversion`
   * and `UpdateReceipt` to another player's event stream.
   *
   * @param entryTimestampsGetter A function that returns a promise of entry timestamps for given player IDs.
   * In case of missing data, omit player IDs from the return value.
   */
  setEntryTimestampsGetter(entryTimestampsGetter) {
    this.entryTimestampsGetter = entryTimestampsGetter;
  }
  getPurchaseProviderInfo() {
    let purchaseProvider;
    let purchaseProviderMethod;
    if (this.osType === "ANDROID_APP") {
      purchaseProvider = "google";
      purchaseProviderMethod = "google_native";
    } else if (this.osType === "IOS_APP") {
      purchaseProvider = "apple";
      purchaseProviderMethod = "apple_native";
    } else if (this.platformID === "fb") {
      purchaseProvider = "facebook";
      purchaseProviderMethod = "facebook_app";
    } else {
      throw Error("Cannot resolve purchase provider");
    }
    return { purchaseProvider, purchaseProviderMethod };
  }
  getExtendedPurchaseEventProperties(product) {
    const config = {
      convertCurrency: this.convertCurrency,
      revenueCurrency: this._gameConfig.revenueCurrency
    };
    return getExtendedPurchaseEventProperties(
      product,
      this.getPurchaseProviderInfo(),
      config
    );
  }
  async getTemporalUserProperties(playerIds) {
    var _a;
    if (!playerIds.length) {
      return {};
    }
    if (!this.entryTimestampsGetter) {
      return {};
    }
    try {
      const entryTimestamps = await this.entryTimestampsGetter(playerIds);
      const now = globalTime.getNow();
      const temporalUserProps = {};
      for (const playerId of playerIds) {
        if (!entryTimestamps[playerId] || Object.keys(entryTimestamps[playerId]).length === 0) {
          continue;
        }
        const userTemporalUserProps = getTemporalUserPropertiesFromEntryTimestamps(
          entryTimestamps[playerId],
          now,
          (_a = this._gameConfig) == null ? void 0 : _a.amplitudeTimeZone
        );
        if (userTemporalUserProps) {
          temporalUserProps[playerId] = userTemporalUserProps;
        } else {
          trackGetEntryTimestampsFailure(
            Error("Invalid entry timestamps"),
            {
              entryTimestamps: entryTimestamps[playerId],
              receiverID: playerId
            }
          );
        }
      }
      return temporalUserProps;
    } catch (error) {
      trackGetEntryTimestampsFailure(error);
      return {};
    }
  }
  // TODO inApp purchases
  // TODO session
  /**
   * Post an update to the current context, for example a message in a chat thread.
   *
   * Define `opts.receiver` in order to track [`UpdateReceiptSuccess`](https://docs.dev.gc-internal.net/gcinstant/events-reference#updatereceiptsuccess).
   *
   * @throws `INVALID_PARAM` error on the LINE platform `opts.template` is not defined.
   * @throws `INVALID_PARAM` error on the Telegram platform if already in a chat thread with the update receiver.
   * @throws If the platform SDK call fails for any other reason.
   */
  updateAsync(opts) {
    console.log("FBInstant.updateAsync", opts);
    const payloadData = this._populateNotificationPayload(opts.data, "UPDATE");
    return Promise.resolve({ payloadData, returnValue: void 0 });
  }
  isContextSizeBetween(min, max) {
    return null;
  }
  /**
   * Open a UI dialog to let the user share specified content.
   *
   * Exact sharing behavior varies per platform:
   * - FB: creates a post on the users's Facebook wall.
   * - Link: opens a friend picker UI dialog and sends a message to selected friends.
   * - LINE: opens a friend picker UI dialog and sends a message to selected friends, unless `opts.receiverIDs` is specified in which case sends message to specified receivers.
   * - Telegram: not implemented, use `shareStoryAsync` instead.
   *
   * @throws `INVALID_PARAM` error on the LINE platform if `opts.receiverIDs`
   * includes a non-friend ID or `opts.template` is not defined.
   * @throws `USER_INPUT` error on the LINE platform if user cancels the UI dialog before completing the share.
   * Link platform does not throw the error, and on Facebook the error is only thrown if `opts.switchContext` is `true`.
   * @throws If the platform SDK call fails for any other reason.
   *
   * @returns receiver IDs on LINE and `undefined` on other platforms.
   */
  shareAsync(opts) {
    console.log("FBInstant.shareAsync", opts);
    return Promise.resolve();
  }
  /**
   * Share a game link with a payload.
   *
   * Exact sharing behavior varies per platform:
   * - FB: invokes a dialog that contains a custom game link that users can copy to their clipboard, or share.
   *   The provided text and image will be used to generate the link preview, with the game name as the title of the preview.
   *   The returned promise will resolve when the dialog is closed regardless if the user actually shared the link or not.
   * - Telegram: copies an invite link to clipboard.
   */
  shareLinkAsync(opts) {
    console.log("FBInstant.shareLinkAsync", opts);
    return Promise.resolve();
  }
  /**
   * Opens the native story editor with the media specified in the `image` parameter. Note that only premium subscribers can post stories with links.
   *
   * Only enabled on the Telegram platform.
   */
  shareStoryAsync(opts) {
    throw createError("INVALID_OPERATION", "Not implemented");
  }
  /**
   * Only enabled on the Facebook, LINE, and Telegram platforms.
   *
   * On Telegram, only the provided data and text options are used. See the `telegramMiniAppName`
   * option for customizing the URL. The bot username must be configured by setting the
   * `TELEGRAM_BOT_USERNAME` environment variable or passing config.telegramBotUsername.
   *
   * @throws `INVALID_PARAM` error on the LINE platform if `opts.receiverIDs`
   * includes a non-friend ID  or `opts.template` is not defined.
   * @throws `USER_INPUT` error on the LINE platform if user closes the friend
   * selection or confirmation dialog without sending any messages. Does not
   * throw `USER_INPUT` on the Facebook platform.
   *
   * @returns receiver IDs on LINE and void on other platforms.
   */
  inviteAsync(opts) {
    console.log("FBInstant.inviteAsync", opts);
    return Promise.resolve();
  }
  /**
   * Returns any data object associated with the entry point that the game was launched from.
   *
   * The contents of the object are developer-defined, and can occur from entry points on
   * different platforms. This will return null for older mobile clients, as well as when
   * there is no data associated with the particular entry point.
   */
  getEntryPointData() {
    if (this.entryData !== null) {
      return this.entryData;
    }
    this.entryData = this._getEntryPointDataForce() ?? {};
    return this.entryData;
  }
  /** Get platform entry point data, skipping cache. */
  _getEntryPointDataForce() {
    return void 0;
  }
  /**
   * Decodes URL encoded entry data. URL looks like `https://www.example.com?payload=%7B%22key%22%3A%22value%22%7D`.
   *
   * To encode entry data use `encodeURIComponent(JSON.stringify(data))` (standard ES5 function) and append it to the game's URL.
   *
   * To get entry data within the game's code, use
   *
   * ```ts
   * platform.getEntryDataAsync().then(entryData => {
   *   // Check entryData here
   * });
   * ```
   *
   * NOTE that only the simulator/mock platform and LINE use URL encoded entry data, other platforms have their own ways
   * however we use it in local facebook env development too to add custom payload to simulate prod payloaded entrypoints
   */
  _getQueryEntryData() {
    const queryString = decodeURIComponent(
      decodeURIComponent(window.location.search.slice(1))
    );
    const params = queryString.split("&");
    const paramMap = {};
    for (let p = 0; p < params.length; p++) {
      const param = params[p].split("=");
      const key = param[0];
      const value = param[1];
      paramMap[key] = value;
      if (value === "null" || value === void 0) {
        paramMap.tID = key;
      }
    }
    for (const entryProperty in paramMap) {
      const entryValue = paramMap[entryProperty];
      if (entryValue == null ? void 0 : entryValue.endsWith("=")) {
        paramMap[entryProperty] = entryValue.slice(0, entryValue.length - 1);
      }
    }
    const payload = paramMap.payload;
    const entryData = payload ? JSON.parse(payload) : {};
    return entryData;
  }
  async getEntryPointAsync() {
    this.entryPointName = "MOCK";
    return this.entryPointName;
  }
  getEntryDataAsync() {
    return this._entryDataPromise.then(() => this.entryData);
  }
  /**
   * Returns timestamp of previous entry, or `null` if currently on first entry.
   *
   * @throws if called before resolving `loadStorage`.
   */
  getPreviousEntryTimestamp() {
    this.throwIfStorageIsNotLoaded();
    return this.previousEntryTimestamp;
  }
  /**
   * @returns ID of the referring player, or `undefined` if game entry was not from a referral.
   * @throws `INVALID_OPERATION` if called before resolving `Platform.startGameAsync`.
   */
  getReferrerID() {
    this.throwIfNotGameStarted();
    return this.entryData.playerID;
  }
  getLeaderboardAsync(leaderboardId) {
    return Promise.resolve({});
  }
  postSessionScoreAsync(score, analyticsData) {
    return Promise.reject(
      Error("GCInstant.postSessionScoreAsync: Not implemented")
    );
  }
  /**
   * Return contextual payload properties that the Platform can provide.
   *
   * Should be called after `sendEntryFinalAnalytics`.
   */
  getPlatformPayloadData() {
    var _a;
    const firstProps = this.storage.analytics;
    const lastProps = internalAnalytics.getUserProperties();
    const abTestPayloadProperties = {
      ...this.externalABTests.getPayloadProperties()
    };
    if ((_a = this.abTests) == null ? void 0 : _a.config) {
      this.abTests.applyPayloadProperties(abTestPayloadProperties);
    }
    const now = globalTime.getNow();
    const maxReferralChainDuration = duration({ days: 1 });
    const sourceChainTimestamps = (this.entryData.sourceChainTimestamps || []).filter((timestamp) => now - timestamp <= maxReferralChainDuration).concat(now);
    const date = new Date(now).toISOString().slice(0, 10).replace(/-/g, "");
    const {
      date: projectDate,
      dateHour,
      dateMonth,
      dateTime,
      dateWeek,
      dayOfWeek,
      hour
    } = getAnalyticsTimeProperties(now, this._gameConfig.amplitudeTimeZone);
    return {
      ...abTestPayloadProperties,
      ABTag: lastProps.ABtag,
      country: this.countryName,
      date,
      dateHour,
      dateMonth,
      dateTime,
      dateWeek,
      dayOfWeek,
      daysSinceInstall: lastProps.daysSinceInstall,
      firstEntryABTests: firstProps.firstEntryABTests,
      hour,
      playerID: this.playerID,
      projectDate,
      senderID: this.playerID,
      sourceChainTimestamps,
      sourceVersion: this._gameConfig.version,
      sourceOS: userAgent.os.name,
      timestamp: now,
      $firstConnectedPlayerCount: firstProps.firstEntryConnectedPlayerCount,
      $firstEntryChannel: firstProps.$firstEntryChannel,
      $firstEntryDate: firstProps.firstEntryDate,
      $firstEntryPointName: firstProps.firstEntryPointName,
      $firstEntryVersion: firstProps.firstEntryVersion,
      firstEntryAdCampaignName: firstProps.firstEntryAdCampaignName,
      firstEntryAdCreativeName: firstProps.firstEntryAdCreativeName,
      firstEntryAdNetworkName: firstProps.firstEntryAdNetworkName,
      firstEntryAdSetName: firstProps.firstEntryAdSetName,
      $lastConnectedPlayerCount: lastProps.lastConnectedPlayerCount,
      $lastEntrySessionMaturityRange: lastProps.$lastEntrySessionMaturityRange,
      $lastEntryChannel: lastProps.$lastEntryChannel,
      $lastEntryPlatform: lastProps.$lastEntryPlatform,
      $lastEntryPointName: lastProps.lastEntryPointName,
      $lastEntryVersion: lastProps.lastEntryVersion,
      lastEntryBrowser: lastProps.lastEntryBrowser,
      lastEntryTimezone: lastProps.lastEntryTimezone,
      $firstEntryGeneration: firstProps.$firstEntryGeneration,
      $zeroEntryChannel: firstProps.$zeroEntryChannel,
      $zeroEntryDatetime: firstProps.$zeroEntryDatetime,
      $zeroEntryDate: firstProps.$zeroEntryDate,
      $zeroEntryAdCampaignName: firstProps.$zeroEntryAdCampaignName,
      $zeroEntryAdSetName: firstProps.$zeroEntryAdSetName,
      $zeroEntryAdCreativeName: firstProps.$zeroEntryAdCreativeName,
      $zeroEntryAdNetworkName: firstProps.$zeroEntryAdNetworkName,
      zeroEntrySourcePlayerID: firstProps.zeroEntrySourcePlayerID,
      $entryCount: lastProps.entryCount,
      lastEntryActiveClusterSize1D: lastProps.lastEntryActiveClusterSize1D,
      lastEntryActiveClusterSize3D: lastProps.lastEntryActiveClusterSize3D,
      lastEntryActiveClusterSize7D: lastProps.lastEntryActiveClusterSize7D,
      lastEntryActiveClusterSize14D: lastProps.lastEntryActiveClusterSize14D,
      lastEntryActiveClusterSize30D: lastProps.lastEntryActiveClusterSize30D,
      lastEntryActiveClusterSize90D: lastProps.lastEntryActiveClusterSize90D,
      lastEntryActiveClusterConnections1D: lastProps.lastEntryActiveClusterConnections1D,
      lastEntryActiveClusterConnections3D: lastProps.lastEntryActiveClusterConnections3D,
      lastEntryActiveClusterConnections7D: lastProps.lastEntryActiveClusterConnections7D,
      lastEntryActiveClusterConnections14D: lastProps.lastEntryActiveClusterConnections14D,
      lastEntryActiveClusterConnections30D: lastProps.lastEntryActiveClusterConnections30D,
      lastEntryActiveClusterConnections90D: lastProps.lastEntryActiveClusterConnections90D,
      lastEntryActiveClusterConnectedness1D: lastProps.lastEntryActiveClusterConnectedness1D,
      lastEntryActiveClusterConnectedness3D: lastProps.lastEntryActiveClusterConnectedness3D,
      lastEntryActiveClusterConnectedness7D: lastProps.lastEntryActiveClusterConnectedness7D,
      lastEntryActiveClusterConnectedness14D: lastProps.lastEntryActiveClusterConnectedness14D,
      lastEntryActiveClusterConnectedness30D: lastProps.lastEntryActiveClusterConnectedness30D,
      lastEntryActiveClusterConnectedness90D: lastProps.lastEntryActiveClusterConnectedness90D,
      lastEntryAgeConfidence: this.storage.demographics.ageConfidence,
      lastEntryAgeRange: this.storage.demographics.ageRange ? ageRangeToString(this.storage.demographics.ageRange) : void 0,
      lastEntryGender: this.storage.demographics.gender,
      lastEntryAgeIsAdult: this.storage.demographics.ageRange ? isAdult(this.storage.demographics.ageRange) : void 0,
      realtimeActiveFriendCount1D: lastProps.lastEntryActiveFriendCount1D,
      realtimeActiveFriendCount3D: lastProps.lastEntryActiveFriendCount3D,
      realtimeActiveFriendCount7D: lastProps.lastEntryActiveFriendCount7D,
      realtimeActiveFriendCount14D: lastProps.lastEntryActiveFriendCount14D,
      realtimeActiveFriendCount30D: lastProps.lastEntryActiveFriendCount30D,
      realtimeActiveFriendCount90D: lastProps.lastEntryActiveFriendCount90D
    };
  }
  /**
   * This populates a notification payload with information that the Platform can provide
   * and that is required for analytics.
   */
  _populateNotificationPayload(payload, $channel) {
    sessionSequence += 1;
    const absoluteSequence = this.storage.absoluteSequence + 1;
    this.storage.assign(
      (storage) => storage.absoluteSequence = absoluteSequence
    );
    const feature = payload.feature;
    const featureSessionSequence = (featureSessionSequences[feature] || 0) + 1;
    featureSessionSequences[feature] = featureSessionSequence;
    const featureAbsoluteSequence = (this.storage.featureAbsoluteSequences[feature] || 0) + 1;
    this.storage.assign(
      (storage) => storage.featureAbsoluteSequences[feature] = featureAbsoluteSequence
    );
    const tracerKey = [
      this.playerID,
      globalTime.getNow(),
      Math.random().toString(36).substr(2, 5)
    ].join("-");
    return {
      ...payload,
      ...this.getPlatformPayloadData(),
      timestampIsFromServer: getAdjustedNow().timestampIsFromServer,
      sessionSequence,
      absoluteSequence,
      featureSessionSequence,
      featureAbsoluteSequence,
      tracerKey,
      $channel
    };
  }
  /**
   * Loads platform storage.
   *
   * When using a storage adapter, make sure to configure the adapter
   * before invoking this method.
   *
   * Subsequent calls return the initial call's cached return value, so this method can
   * be called several times without extra side-effects.
   *
   * @throws If storage adapter is not configured when in production mode.
   *
   * @returns A promise that resolves once storage is loaded.
   */
  async loadStorage() {
    this.throwIfNotInitialized();
    this.throwIfStorageAdapterIsNotConfigured();
    if (this._loadStoragePromise) {
      return this._loadStoragePromise;
    }
    this._loadStoragePromise = this.storage.load().then(() => {
      if (this.shouldIncreaseEntryCount()) {
        this.storage.assign((storage) => storage.entry.count += 1);
      }
      const isNewUser = this.storage.entry.count === 1;
      if (isNewUser) {
        this.storage.assign(
          (storage) => storage.tos["2022-12"] = { acceptedAt: globalTime.getNow() }
        );
      }
      if (this.storage.entry.last) {
        this.previousEntryTimestamp = this.storage.entry.last;
      }
      const appName = this._gameConfig.shortName;
      this.abTests = new ABTests(
        this.playerID,
        appName,
        this.storage,
        () => this.externalABTests.getABTag()
      );
    });
    return this._loadStoragePromise;
  }
  _createEntryPromise() {
    return Promise.resolve();
  }
  _setEntryDataUserProperties(extraUserProperties) {
    const userProperties = {
      ...extraUserProperties
    };
    const entryData = this.entryData;
    userProperties.lastEntrySourceFeature = entryData.feature;
    userProperties.lastEntrySubFeature = entryData.$subFeature;
    userProperties.lastEntrySourceVersion = entryData.sourceVersion;
    userProperties.lastEntrySourceGameFeature = entryData.gameFeature ?? entryData.sourceGameFeature;
    userProperties.lastEntrySourceGameID = entryData.gameID;
    userProperties.lastEntrySourceGamePlayerID = entryData.gamePlayerID;
    userProperties.lastEntrySourcePlayerID = entryData.playerID;
    userProperties.lastEntryChannelIsInferred = !this.entryData.$channel;
    userProperties.lastEntryChannelIsSocial = isSocialChannel(
      this.getChannel()
    );
    internalAnalytics.setUserProperties(userProperties, internalAnalytics.toDefaultAndAdServer);
  }
  _setCanSubscribeToBot(value) {
    internalAnalytics.setUserProperties({ canSubscribeBot: value });
    this.canSubscribeToBot = value;
  }
  _setContextSquad(squad) {
    internalAnalytics.setUserProperties({ contextIsSquad: !!squad });
    this.contextSquad = squad;
  }
  _setContextTournament(contextTournament) {
    internalAnalytics.setUserProperties({ contextIsTournament: !!contextTournament });
    this.contextTournament = contextTournament;
  }
  _setIsSubscribedToBot(value) {
    internalAnalytics.setUserProperties({ isSubscribed: value });
    this.isSubscribedToBot = value;
  }
  _updateContext(contextAPI, context) {
    this._setContextTournament(null);
    this._setContextSquad(null);
    this.contextID = context.getID();
    this.contextType = context.getType();
    const updateContextAPI = contextAPI !== "Launch" || this.contextID;
    internalAnalytics.setUserProperties(
      {
        contextID: this.contextID,
        contextType: this.contextType,
        ...updateContextAPI ? { contextAPI } : {}
      },
      internalAnalytics.toDefaultAndAdServer
    );
    if (contextAPI !== "Launch" && contextAPI !== "GetTournamentAsync") {
      this.afterContextSwitchCallbacks.forEach((callback) => callback());
    }
  }
  _populateEntryDataEventProperties(eventProperties) {
    var _a, _b;
    const entryData = this.entryData;
    for (const entryProp in entryData) {
      if (entryProp.startsWith("sourceAB_")) {
        eventProperties[entryProp] = entryData[entryProp];
      }
    }
    eventProperties.$sourceFirstEntryVersion = entryData.$firstEntryVersion;
    eventProperties.$wasEntryDataReadyEarly = this.wasEntryDataReadyEarly;
    eventProperties.adSetAdName = entryData.$adSetName && entryData.$adCreativeName ? `${entryData.$adSetName};${entryData.$adCreativeName}` : void 0;
    eventProperties.absoluteSequence = entryData.absoluteSequence;
    eventProperties.chatbotTemplate = entryData.template;
    eventProperties.creativeAssetID = entryData.creativeAssetID ?? entryData.$creativeAssetID;
    eventProperties.creativeCTA = entryData.creativeCTA ?? entryData.$creativeCTA;
    eventProperties.creativeTextID = entryData.creativeTextID ?? entryData.$creativeTextID;
    eventProperties.isAppSwitch = entryData.isAppSwitch;
    eventProperties.sessionSequence = entryData.sessionSequence;
    eventProperties.featureSessionSequence = entryData.featureSessionSequence;
    eventProperties.featureAbsoluteSequence = entryData.featureAbsoluteSequence;
    eventProperties.sourceABTag = entryData.ABTag;
    eventProperties.sourceFirstEntryAdCampaignName = entryData.firstEntryAdCampaignName;
    eventProperties.sourceFirstEntryAdCreativeName = entryData.firstEntryAdCreativeName;
    eventProperties.sourceFirstEntryAdNetworkName = entryData.firstEntryAdNetworkName;
    eventProperties.sourceFirstEntryAdSetName = entryData.firstEntryAdSetName;
    eventProperties.sourceDate = entryData.date;
    eventProperties.sourceDateHour = entryData.dateHour;
    eventProperties.sourceDateMonth = entryData.dateMonth;
    eventProperties.sourceDateTime = entryData.dateTime;
    eventProperties.sourceDateWeek = entryData.dateWeek;
    eventProperties.sourceDayOfWeek = entryData.dayOfWeek;
    eventProperties.sourceCountry = entryData.country;
    eventProperties.sourceFirstEntryABTests = entryData.firstEntryABTests;
    eventProperties.sourceHour = entryData.hour;
    eventProperties.sourceLastEntryBrowser = entryData.lastEntryBrowser;
    eventProperties.sourceLastEntryPlatform = entryData.$lastEntryPlatform;
    eventProperties.sourceLastEntrySessionMaturityRange = entryData.$lastEntrySessionMaturityRange;
    eventProperties.sourceLastEntryTimezone = entryData.lastEntryTimezone;
    eventProperties.sourceProjectDate = entryData.projectDate;
    eventProperties.sourceSelectionUI = entryData.selectionUI;
    eventProperties.sourceShareDestination = entryData.destination;
    eventProperties.sourceTournamentAPI = entryData.tournamentAPI;
    eventProperties.sourceTracerKey = entryData.tracerKey;
    eventProperties.sourceUpdatePushRequested = entryData.pushRequested;
    eventProperties.sourceUpdateStrategy = entryData.strategy;
    eventProperties.sourceUpdateTemplateID = entryData.templateID;
    eventProperties.timestampIsFromServer = getAdjustedNow().timestampIsFromServer;
    eventProperties.tournamentPayloadMode = this.tournamentPayloadMode;
    eventProperties.sourceRealtimeActiveFriendCount1D = entryData.realtimeActiveFriendCount1D;
    eventProperties.sourceRealtimeActiveFriendCount3D = entryData.realtimeActiveFriendCount3D;
    eventProperties.sourceRealtimeActiveFriendCount7D = entryData.realtimeActiveFriendCount7D;
    eventProperties.sourceRealtimeActiveFriendCount14D = entryData.realtimeActiveFriendCount14D;
    eventProperties.sourceRealtimeActiveFriendCount30D = entryData.realtimeActiveFriendCount30D;
    eventProperties.sourceRealtimeActiveFriendCount90D = entryData.realtimeActiveFriendCount90D;
    if (this.platformID === "line-guest") {
      eventProperties.isGuest = true;
    }
    eventProperties.channelIsSocial = isSocialChannel(this.getChannel());
    eventProperties.sourceDaysSinceInstall = entryData.daysSinceInstall;
    eventProperties.sourceGameFeature = entryData.gameFeature ?? entryData.sourceGameFeature;
    eventProperties.sourceGameID = entryData.gameID;
    eventProperties.sourceGamePlayerID = entryData.gamePlayerID;
    eventProperties.sourceGameVersion = entryData.gameVersion;
    eventProperties.sourcePlayerID = entryData.playerID;
    eventProperties.sourceVersion = entryData.sourceVersion;
    eventProperties.sourceLevel = entryData.sourceLevel;
    eventProperties.sourceLevelRange = entryData.sourceLevelRange;
    eventProperties.sourceFeature = entryData.feature;
    eventProperties.sourceSubFeature = entryData.$subFeature;
    eventProperties.sourceSelectionCount = entryData.selectionCount;
    eventProperties.$subFeature = entryData.$subFeature;
    eventProperties.webShareLinkId = entryData.webShareLinkId;
    eventProperties.webShareOrigin = entryData.webShareOrigin;
    eventProperties.webReferrer = entryData.webReferrer ?? document.referrer;
    eventProperties.locationHref = this.originalLocationHref ?? "";
    eventProperties.locationHostname = location.hostname;
    eventProperties.locationReferrer = document.referrer;
    if (entryData.receivers) {
      eventProperties.sourceUpdateIsToPlayer = (_a = entryData.receivers.find(
        (receiver) => receiver.id === this.playerID
      )) == null ? void 0 : _a.isPlayer;
    }
    const lastReceivedChatbotMessagePayload = ((_b = this.lastReceivedChatbotMessagePayload) == null ? void 0 : _b.timestamp) > globalTime.getNow() - duration({ hours: 2 }) ? this.lastReceivedChatbotMessagePayload : void 0;
    if (lastReceivedChatbotMessagePayload) {
      const { feature, $subFeature, $creativeAssetID, creativeAssetID } = lastReceivedChatbotMessagePayload;
      eventProperties.latestChatbotSourceFeature = feature;
      eventProperties.latestChatbotSourceSubFeature = $subFeature;
      eventProperties.latestChatbotCreativeAssetID = creativeAssetID ?? $creativeAssetID;
    }
  }
  /**
   * Resolves after entry analytics have been sent
   */
  waitForEntryAnalytics() {
    return this._entryAnalyticsPromise;
  }
  _sendEntryAnalytics() {
    const resolveEntryAnalyticsPromise = this._resolveEntryAnalytics;
    if (!resolveEntryAnalyticsPromise) {
      throw Error("Entry analytics have already been sent");
    }
    this._resolveEntryAnalytics = null;
    Promise.all([
      this._createEntryPromise(),
      this.loadStorage().then(() => void this.trackTimeTo("loadStorage"))
    ]).catch((error) => {
      internalAnalytics.pushError("EntryError", error);
      return Promise.resolve();
    }).then(() => {
      const entryCount = this.storage.entry.count;
      const now = globalTime.getNow();
      const utcDate = new Date(now).toISOString().slice(0, 10).replace(/-/g, "");
      const lastEntryTime = this.storage.entry.last;
      const lastEntryDay = new Date(lastEntryTime);
      const today = new Date(now);
      const isFirstEntryOfDay = today.getDate() !== lastEntryDay.getDate() || today.getMonth() !== lastEntryDay.getMonth() || today.getFullYear() !== lastEntryDay.getFullYear();
      const {
        date: projectDate,
        dateHour,
        dateMonth,
        dateTime,
        dateWeek,
        dayOfWeek,
        hour
      } = getAnalyticsTimeProperties(now, this._gameConfig.amplitudeTimeZone);
      const sharedUserProps = {
        contextID: this.contextID,
        contextType: this.contextType,
        contextIsSquad: !!this.contextSquad,
        contextIsTournament: !!this.contextTournament,
        entryCount,
        adFeatureAvailable: this.canShowInterstitialAds || this.canShowRewardedVideoAds,
        lastEntryIsFirstOfDay: isFirstEntryOfDay,
        lastEntryPaymentsEnabled: this.paymentsAvailable,
        lastEntryVersion: this._gameConfig.version,
        lastEntryDate: utcDate,
        lastEntryDateHour: dateHour,
        lastEntryDateMonth: dateMonth,
        lastEntryDateTime: dateTime,
        lastEntryDateWeek: dateWeek,
        lastEntryDayOfWeek: dayOfWeek,
        lastEntryHour: hour,
        lastEntryProjectDate: projectDate,
        lastEntryTimezone: getTimezoneName()
      };
      this._setEntryDataUserProperties(sharedUserProps);
      const entryEventsBundle = {
        first: entryCount === 1,
        date: utcDate,
        dateHour,
        dateMonth,
        dateTime,
        dateWeek,
        dayOfWeek,
        connectedPlayerCount: this.friends.length,
        hour,
        entryPointName: this.entryPointName,
        isFirstEntryOfDay,
        // TODO: remove (deprecated, is now a user property)
        adFeatureAvailable: this.canShowInterstitialAds || this.canShowRewardedVideoAds
      };
      this._populateEntryDataEventProperties(entryEventsBundle);
      internalAnalytics.pushEvent(
        "Entry",
        entryEventsBundle,
        internalAnalytics.toDefaultAndAdServer
      );
      this.entryAnalyticsBundle = entryEventsBundle;
    }).then(resolveEntryAnalyticsPromise.resolve).catch((err) => {
      resolveEntryAnalyticsPromise.reject(err);
      throw err;
    });
    return this._entryAnalyticsPromise;
  }
  /**
   * Tracks the first button tap. Call this when a button is clicked.
   *
   * @param analyticsData Any custom analytics the game wants to track.
   */
  trackFirstButtonTap(analyticsData) {
    if (this.didTrackFirstButtonTap) {
      return;
    }
    this.didTrackFirstButtonTap = true;
    trackFirstButtonTap({ occasion: "sessionStart", ...analyticsData });
  }
  /**
   * Track a [`Pinch`](https://docs.dev.gc-internal.net/gcinstant/events-reference/#pinch) event.
   *
   * Track `Pinch` when user enters a state of pinch, i.e., runs out of a consumable resource.
   * When the pinch state is resolved, track a matching `PinchConversion` event with `trackPinchConversion`.
   */
  trackPinch(analyticsData) {
    this.pinch.trackPinch(analyticsData);
  }
  /**
   * Track a [`PinchConversion`](https://docs.dev.gc-internal.net/gcinstant/events-reference/#pinchconversion) event.
   *
   * Track `PinchConversion` when the user performs an action (such as an invite or a purchase) that earns them a resource in a state of a pinch.
   *
   * The `PinchConversion` event is automatically associated with a `Pinch` event tracked earlier in the session when the pinched resources match.
   *
   * @throws if called before loading analytics storage, i.e., before resolving `Platform.waitForEntryAnalytics()`.
   */
  trackPinchConversion(opts) {
    this.throwIfStorageIsNotLoaded();
    this.pinch.trackPinchConversion(opts);
  }
  /**
   * Set entry user properties and send the canonical `EntryFinal` analytics event.
   * Call this after finishing all initialization and starting the game.
   *
   * GCInstant sets a number of event and user properties internally; use the
   * parameters to pass in additional game-specific properties.
   *
   * @throws if called more than once.
   *
   * @param entryFinalEventProperties Extra event properties of the `EntryFinal` event.
   * @param firstEntryUserProperties Extra user properties set for new users.
   * @param lastEntryUserProperties Extra user properties set for all users.
   */
  sendEntryFinalAnalytics(entryFinalEventProperties, firstEntryUserProperties, lastEntryUserProperties) {
    this.throwIfSendEntryFinalAnalyticsIsCalledMultipleTimes();
    return this.waitForEntryAnalytics().then(async () => {
      var _a, _b, _c, _d, _e, _f;
      const entryData = this.entryData;
      const entryEventsBundle = this.entryAnalyticsBundle;
      if (!entryEventsBundle) {
        throw Error("Base Entry event properties are not set");
      }
      this._setEntryDataUserProperties();
      this._populateEntryDataEventProperties(entryEventsBundle);
      const $channel = this.getChannel();
      const $channelIsInferred = !this.entryData.$channel;
      const $platform = this.osType;
      let $adNetworkName = entryData.$adNetworkName;
      if (!entryData.$adNetworkName) {
        if ((_a = entryData.$adCampaignName) == null ? void 0 : _a.startsWith("FB ")) {
          $adNetworkName = "Facebook";
        } else if ((_b = entryData.$adCampaignName) == null ? void 0 : _b.startsWith("IG ")) {
          $adNetworkName = "Instagram";
        }
      }
      const now = globalTime.getNow();
      const entry = this.storage.entry;
      const lastEntryTime = entry.last;
      let lastFBNativeEntry = entry.lastNativeEntry;
      this.storage.assign((storage) => storage.entry.last = now);
      if (entry.count === 1) {
        this.storage.assign((storage) => storage.entry.first = now);
      }
      if ($channel === "AD") {
        this.storage.assign((storage) => storage.entry.lastAdEntry = now);
      }
      if (entryData.applePushEnabled !== void 0) {
        this.storage.assign((storage) => storage.entry.lastNativeEntry = now);
        lastFBNativeEntry = lastFBNativeEntry || now;
      }
      const isNativeEntry = this.osType === "IOS_APP" || this.osType === "ANDROID_APP";
      const isFirstNativeEntry = isNativeEntry && this.storage.entry.lastNativeAndroidEntry === 0 && this.storage.entry.lastNativeIOSEntry === 0;
      if (this.osType === "ANDROID_APP") {
        this.storage.assign(
          (storage) => storage.entry.lastNativeAndroidEntry = now
        );
      } else if (this.osType === "IOS_APP") {
        this.storage.assign(
          (storage) => storage.entry.lastNativeIOSEntry = now
        );
      }
      const daysElapsedFirstEntry = (now - entry.first) / dayInMs;
      const hoursInactive = lastEntryTime === 0 ? 0 : (now - lastEntryTime) / 1e3 / 60 / 60;
      const daysInactive = hoursInactive / 24;
      const isResurrectedUser = daysInactive >= 90;
      const daysSinceLastResurrected = entry.lastResurrected ? Math.floor((now - entry.lastResurrected) / dayInMs) : void 0;
      if (isResurrectedUser) {
        this.storage.assign((storage) => storage.entry.lastResurrected = now);
      }
      const { currentPayloadIdentifier, lastEntryIsRepeat } = getLastEntryIsRepeat({
        entryData: this.entryData,
        entryPointName: this.entryPointName,
        hoursInactive,
        previousPayloadIdentifier: this.storage.previousPayload
      });
      this.storage.assign(
        (storage) => storage.previousPayload = currentPayloadIdentifier
      );
      const newDemographics = calculateNewDemographics({
        entryData,
        friendAgeGroupDistribution: this.friendAgeGroupDistribution,
        lastEntryIsRepeat,
        storage: this.storage,
        userId: this.playerID
      });
      if (Object.keys(newDemographics).length > 0) {
        this.storage.assign((storage) => {
          if (newDemographics.ageConfidence) {
            storage.demographics.ageConfidence = newDemographics.ageConfidence;
          }
          if (newDemographics.ageRange) {
            storage.demographics.ageRange = newDemographics.ageRange;
          }
          if (newDemographics.gender) {
            storage.demographics.gender = newDemographics.gender;
          }
        });
      }
      await Promise.all(
        this.beforeEntryFinalCallbacks.map((callback) => callback())
      );
      const browserType = this.platformID !== "line" ? void 0 : this.insideNativeIOS ? "NATIVE" : this.osType === "ANDROID" || this.osType === "IOS" ? "LIFF" : userAgent.app.name === "LINE" ? "IAB" : "EXTERNAL";
      const isFirstEntry = entryEventsBundle.first;
      if (isFirstEntry) {
        const generationProperties = getNextGenerationProperties(
          { ...this.entryData, $adNetworkName },
          // Avoid mutating this.entryData.$adNetworkName
          now,
          { channel: $channel, playerID: this.playerID }
        );
        const lastProps = internalAnalytics.getUserProperties();
        const firstEntryABTests = deduplicate([
          ...((_c = this.abTests) == null ? void 0 : _c.getAssignedNonPlaceboTestIds()) || [],
          ...this.externalABTests.getAssignedNonPlaceboTestIds()
        ]).sort();
        const firstEntryProps = {
          ...firstEntryUserProperties,
          ...generationProperties,
          $firstEntryChannel: $channel,
          $firstEntryPlatform: $platform,
          $firstEntrySourceLastEntryPlatform: entryData.$lastEntryPlatform,
          $firstEntrySourceLastEntrySessionMaturityRange: entryData.$lastEntrySessionMaturityRange,
          firstEntryActiveClusterSize1D: lastProps.lastEntryActiveClusterSize1D,
          firstEntryActiveClusterSize3D: lastProps.lastEntryActiveClusterSize3D,
          firstEntryActiveClusterSize7D: lastProps.lastEntryActiveClusterSize7D,
          firstEntryActiveClusterSize14D: lastProps.lastEntryActiveClusterSize14D,
          firstEntryActiveClusterSize30D: lastProps.lastEntryActiveClusterSize30D,
          firstEntryActiveClusterSize90D: lastProps.lastEntryActiveClusterSize90D,
          firstEntryActiveClusterConnections1D: lastProps.lastEntryActiveClusterConnections1D,
          firstEntryActiveClusterConnections3D: lastProps.lastEntryActiveClusterConnections3D,
          firstEntryActiveClusterConnections7D: lastProps.lastEntryActiveClusterConnections7D,
          firstEntryActiveClusterConnections14D: lastProps.lastEntryActiveClusterConnections14D,
          firstEntryActiveClusterConnections30D: lastProps.lastEntryActiveClusterConnections30D,
          firstEntryActiveClusterConnections90D: lastProps.lastEntryActiveClusterConnections90D,
          firstEntryActiveClusterConnectedness1D: lastProps.lastEntryActiveClusterConnectedness1D,
          firstEntryActiveClusterConnectedness3D: lastProps.lastEntryActiveClusterConnectedness3D,
          firstEntryActiveClusterConnectedness7D: lastProps.lastEntryActiveClusterConnectedness7D,
          firstEntryActiveClusterConnectedness14D: lastProps.lastEntryActiveClusterConnectedness14D,
          firstEntryActiveClusterConnectedness30D: lastProps.lastEntryActiveClusterConnectedness30D,
          firstEntryActiveClusterConnectedness90D: lastProps.lastEntryActiveClusterConnectedness90D,
          firstEntryActiveFriendCount1D: lastProps.lastEntryActiveFriendCount1D,
          firstEntryActiveFriendCount3D: lastProps.lastEntryActiveFriendCount3D,
          firstEntryActiveFriendCount7D: lastProps.lastEntryActiveFriendCount7D,
          firstEntryActiveFriendCount14D: lastProps.lastEntryActiveFriendCount14D,
          firstEntryActiveFriendCount30D: lastProps.lastEntryActiveFriendCount30D,
          firstEntryActiveFriendCount90D: lastProps.lastEntryActiveFriendCount90D,
          "firstEntryActiveFriendRatio1/3": lastProps["lastEntryActiveFriendRatio1/3"],
          "firstEntryActiveFriendRatio1/7": lastProps["lastEntryActiveFriendRatio1/7"],
          "firstEntryActiveFriendRatio3/7": lastProps["lastEntryActiveFriendRatio3/7"],
          "firstEntryActiveFriendRatio7/30": lastProps["lastEntryActiveFriendRatio7/30"],
          firstEntrySourceRealtimeActiveFriendCount1D: entryData.realtimeActiveFriendCount1D,
          firstEntrySourceRealtimeActiveFriendCount3D: entryData.realtimeActiveFriendCount3D,
          firstEntrySourceRealtimeActiveFriendCount7D: entryData.realtimeActiveFriendCount7D,
          firstEntrySourceRealtimeActiveFriendCount14D: entryData.realtimeActiveFriendCount14D,
          firstEntrySourceRealtimeActiveFriendCount30D: entryData.realtimeActiveFriendCount30D,
          firstEntrySourceRealtimeActiveFriendCount90D: entryData.realtimeActiveFriendCount90D,
          firstEntryAgeConfidence: this.storage.demographics.ageConfidence,
          firstEntryAgeIsAdult: this.storage.demographics.ageRange ? isAdult(this.storage.demographics.ageRange) : void 0,
          firstEntryAgeRange: this.storage.demographics.ageRange ? ageRangeToString(this.storage.demographics.ageRange) : void 0,
          firstEntryGender: this.storage.demographics.gender,
          firstEntryAdCampaignName: entryData.$adCampaignName,
          firstEntryAdCreativeName: entryData.$adCreativeName,
          firstEntryAdNetworkName: entryData.$adNetworkName,
          firstEntryAdSetName: entryData.$adSetName,
          firstEntryBrowser: browserType,
          firstEntryConnectedPlayerCount: entryEventsBundle.connectedPlayerCount,
          firstEntryContextID: entryData.contextID,
          firstEntryABTests,
          firstEntryChannelIsInferred: $channelIsInferred,
          firstEntryChannelIsSocial: isSocialChannel(this.getChannel()),
          firstEntryDate: entryEventsBundle.date,
          firstEntryDateHour: lastProps.lastEntryDateHour,
          firstEntryDateMonth: lastProps.lastEntryDateMonth,
          firstEntryDateTime: lastProps.lastEntryDateTime,
          firstEntryDateWeek: lastProps.lastEntryDateWeek,
          firstEntryDayOfWeek: lastProps.lastEntryDayOfWeek,
          firstEntryHour: lastProps.lastEntryHour,
          firstEntryIsNative: isNativeEntry,
          firstEntryProjectDate: lastProps.lastEntryProjectDate,
          firstEntrySourceFeature: entryData.feature,
          firstEntrySourceGameFeature: entryData.gameFeature ?? entryData.sourceGameFeature,
          firstEntrySourceGameID: entryData.gameID,
          firstEntrySourceGamePlayerID: entryData.gamePlayerID,
          firstEntrySourceGameVersion: entryData.gameVersion,
          firstEntrySourceLevel: entryData.sourceLevel,
          firstEntrySourceLevelRange: entryData.sourceLevelRange,
          firstEntrySourceOS: entryData.sourceOS,
          firstEntrySourcePlayerID: entryData.playerID,
          firstEntrySourceVersion: entryData.sourceVersion,
          firstEntrySubFeature: entryData.$subFeature,
          firstEntryVersion: this._gameConfig.version,
          firstEntryPaymentsEnabled: this.paymentsAvailable,
          firstEntryPointName: entryEventsBundle.entryPointName
        };
        if (isNativeEntry) {
          const version = (_e = (_d = window.Native) == null ? void 0 : _d.app) == null ? void 0 : _e.version;
          if (version) {
            const name = this.osType === "IOS_APP" ? "firstEntryAppleVersion" : "firstEntryAndroidVersion";
            firstEntryProps[name] = version;
          }
        }
        const internalABTag = ((_f = this.abTests) == null ? void 0 : _f.config) ? this.abTests.getAbTag() : void 0;
        const externalABTag = this.externalABTests.getABTag();
        const abTag = [internalABTag, externalABTag].filter((x) => !!x).join(",");
        if (abTag) {
          firstEntryProps.firstEntryABTag = abTag;
        }
        if (this.platformID === "line") {
          firstEntryProps.firstEntrySocialFriendCount = this.socialFriends.length;
        }
        internalAnalytics.setUserProperties(
          firstEntryProps,
          internalAnalytics.toDefaultAndAdServer
        );
        this.storage.assign((storage) => {
          for (const key in firstEntryProps) {
            storage.analytics[key] = firstEntryProps[key];
          }
        });
      }
      const { $zeroEntryDatetime } = this.storage.analytics;
      const $lastEntryElapsedDaysZeroEntry = $zeroEntryDatetime != null ? (now - new Date($zeroEntryDatetime).getTime()) / dayInMs : null;
      const referralChainLengths = getReferralChainLengths(
        entryData.sourceChainTimestamps || [],
        now
      );
      const lastEntryChainLengths = Object.entries(referralChainLengths).reduce(
        (acc, [key, val]) => ({ ...acc, [`lastEntryChainLength${key}`]: val }),
        {}
      );
      const { amplitudeTimeZone } = this._gameConfig;
      const projectDaysSinceInstall = getCalendarDaysBetween({
        fromTimestamp: entry.first,
        ianaTimeZone: amplitudeTimeZone,
        toTimestamp: now
      });
      const projectDaysSinceZeroEntry = $zeroEntryDatetime ? getCalendarDaysBetween({
        fromTimestamp: new Date($zeroEntryDatetime).getTime(),
        ianaTimeZone: amplitudeTimeZone,
        toTimestamp: now
      }) : void 0;
      const lastEntryProps = {
        ...lastEntryUserProperties,
        ...lastEntryChainLengths,
        $lastEntryChannel: $channel,
        $lastEntryPlatform: $platform,
        $lastEntrySessionMaturityRange: getSessionMaturityRange(entry.count),
        $lastEntrySourceLastEntryPlatform: entryData.$lastEntryPlatform,
        $lastEntrySourceLastEntrySessionMaturityRange: entryData.$lastEntrySessionMaturityRange,
        $lastEntryAdCampaignName: entryData.$adCampaignName,
        $lastEntryAdSetName: entryData.$adSetName,
        $lastEntryAdCreativeName: entryData.$adCreativeName,
        $lastEntryAdNetworkName: $adNetworkName,
        $lastEntryElapsedDaysZeroEntry,
        hoursElapsedFirstEntry: daysElapsedFirstEntry * 24,
        hoursElapsedLastEntry: hoursInactive,
        lastEntryBrowser: browserType,
        requestedLocale: this.locale,
        platformLocale: this.platformLocale,
        displayedLocale: this.locale,
        lastConnectedPlayerCount: entryEventsBundle.connectedPlayerCount,
        projectDaysSinceInstall,
        lastEntryCalendarDaysSinceInstall: projectDaysSinceInstall,
        projectDaysSinceZeroEntry,
        lastEntryCalendarDaysSinceZeroEntry: projectDaysSinceZeroEntry,
        lastEntryIsFirstOnNative: isFirstNativeEntry,
        lastEntryIsNative: isNativeEntry,
        lastEntryIsRepeat,
        lastEntryPointName: entryEventsBundle.entryPointName,
        daysSinceInstall: Math.floor(daysElapsedFirstEntry),
        daysSinceLastEntry: Math.floor(daysInactive),
        daysSinceLastAdEntry: entry.lastAdEntry ? Math.floor((now - entry.lastAdEntry) / dayInMs) : void 0,
        daysSinceLastResurrected,
        daysElapsedFirstEntry,
        daysElapsedLastEntry: daysInactive,
        lastEntrySourceRealtimeActiveFriendCount1D: entryData.realtimeActiveFriendCount1D,
        lastEntrySourceRealtimeActiveFriendCount3D: entryData.realtimeActiveFriendCount3D,
        lastEntrySourceRealtimeActiveFriendCount7D: entryData.realtimeActiveFriendCount7D,
        lastEntrySourceRealtimeActiveFriendCount14D: entryData.realtimeActiveFriendCount14D,
        lastEntrySourceRealtimeActiveFriendCount30D: entryData.realtimeActiveFriendCount30D,
        lastEntrySourceRealtimeActiveFriendCount90D: entryData.realtimeActiveFriendCount90D,
        lastEntryAgeConfidence: this.storage.demographics.ageConfidence,
        lastEntryAgeIsAdult: this.storage.demographics.ageRange ? isAdult(this.storage.demographics.ageRange) : void 0,
        lastEntryAgeRange: this.storage.demographics.ageRange ? ageRangeToString(this.storage.demographics.ageRange) : void 0,
        lastEntryGender: this.storage.demographics.gender
      };
      if (isResurrectedUser) {
        lastEntryProps.isResurrectedUser = true;
      }
      if (this.platformID === "line") {
        lastEntryProps.lastEntrySocialFriendCount = this.socialFriends.length;
      }
      const lastNativeEntry = Math.max(
        this.storage.entry.lastNativeAndroidEntry,
        this.storage.entry.lastNativeIOSEntry
      );
      const timeSinceLastNativeEntry = lastNativeEntry === 0 ? Infinity : now - lastNativeEntry;
      lastEntryProps.lastEntryNativeActive1D = timeSinceLastNativeEntry <= duration({ days: 1 });
      lastEntryProps.lastEntryNativeActive3D = timeSinceLastNativeEntry <= duration({ days: 3 });
      lastEntryProps.lastEntryNativeActive7D = timeSinceLastNativeEntry <= duration({ days: 7 });
      lastEntryProps.lastEntryNativeActive14D = timeSinceLastNativeEntry <= duration({ days: 14 });
      lastEntryProps.lastEntryNativeActive30D = timeSinceLastNativeEntry <= duration({ days: 30 });
      lastEntryProps.lastEntryNativeActive90D = timeSinceLastNativeEntry <= duration({ days: 90 });
      internalAnalytics.setUserProperties(
        lastEntryProps,
        internalAnalytics.toDefaultAndAdServer
      );
      const entryFinalEventProps = {
        ...entryEventsBundle,
        ...entryFinalEventProperties,
        inactiveDay5: daysInactive > 5,
        inactiveDay10: daysInactive > 10,
        inactiveDay30: daysInactive > 30,
        inactiveDay90: daysInactive > 90,
        isFirstOrResurrect: isFirstEntry || isResurrectedUser,
        isResurrect: isResurrectedUser,
        sourceShareSwitchContext: entryData.switchContext,
        $channel,
        $channelIsInferred,
        $self: this.getReferrerID() === this.playerID,
        $adCampaignName: entryData.$adCampaignName,
        $adSetName: entryData.$adSetName,
        $adCreativeName: entryData.$adCreativeName,
        $adNetworkName,
        $wasEntryDataFullyReadyEarly: this.wasEntryDataReadyEarly && this.onGameStartRetries.length === 0,
        // First session user properties from the referrer
        $sourceFirstEntryChannel: entryData.$firstEntryChannel,
        $sourceFirstConnectedPlayerCount: entryData.$firstConnectedPlayerCount,
        $sourceFirstEntryDate: entryData.$firstEntryDate,
        $sourceFirstEntryPointName: entryData.$firstEntryPointName,
        // Last session user properties from the referrer
        $sourceLastEntryChannel: entryData.$lastEntryChannel,
        $sourceEntryCount: entryData.$entryCount,
        $sourceLastConnectedPlayerCount: entryData.$lastConnectedPlayerCount,
        $sourceLastEntryPointName: entryData.$lastEntryPointName,
        $sourceLastEntryVersion: entryData.$lastEntryVersion,
        sourceLastEntryActiveClusterSize1D: entryData.lastEntryActiveClusterSize1D,
        sourceLastEntryActiveClusterSize3D: entryData.lastEntryActiveClusterSize3D,
        sourceLastEntryActiveClusterSize7D: entryData.lastEntryActiveClusterSize7D,
        sourceLastEntryActiveClusterSize14D: entryData.lastEntryActiveClusterSize14D,
        sourceLastEntryActiveClusterSize30D: entryData.lastEntryActiveClusterSize30D,
        sourceLastEntryActiveClusterSize90D: entryData.lastEntryActiveClusterSize90D,
        sourceLastEntryActiveClusterConnections1D: entryData.lastEntryActiveClusterConnections1D,
        sourceLastEntryActiveClusterConnections3D: entryData.lastEntryActiveClusterConnections3D,
        sourceLastEntryActiveClusterConnections7D: entryData.lastEntryActiveClusterConnections7D,
        sourceLastEntryActiveClusterConnections14D: entryData.lastEntryActiveClusterConnections14D,
        sourceLastEntryActiveClusterConnections30D: entryData.lastEntryActiveClusterConnections30D,
        sourceLastEntryActiveClusterConnections90D: entryData.lastEntryActiveClusterConnections90D,
        sourceLastEntryActiveClusterConnectedness1D: entryData.lastEntryActiveClusterConnectedness1D,
        sourceLastEntryActiveClusterConnectedness3D: entryData.lastEntryActiveClusterConnectedness3D,
        sourceLastEntryActiveClusterConnectedness7D: entryData.lastEntryActiveClusterConnectedness7D,
        sourceLastEntryActiveClusterConnectedness14D: entryData.lastEntryActiveClusterConnectedness14D,
        sourceLastEntryActiveClusterConnectedness30D: entryData.lastEntryActiveClusterConnectedness30D,
        sourceLastEntryActiveClusterConnectedness90D: entryData.lastEntryActiveClusterConnectedness90D,
        sourceLastEntryAgeConfidence: entryData.lastEntryAgeConfidence,
        sourceLastEntryAgeIsAdult: entryData.lastEntryAgeIsAdult,
        sourceLastEntryAgeRange: entryData.lastEntryAgeRange,
        sourceLastEntryGender: entryData.lastEntryGender,
        hoursInactive: Math.floor(hoursInactive),
        daysInactive: Math.floor(daysInactive),
        applePushEnabled: entryData.applePushEnabled,
        appleDaysElapsedLastEntry: lastFBNativeEntry && (now - lastFBNativeEntry) / dayInMs
      };
      let timestamp = entryData.timestamp;
      if (timestamp) {
        if (typeof timestamp === "string") {
          timestamp = Date.parse(timestamp);
          entryData.timestamp = timestamp;
        }
        const elapsedSecs = (now - timestamp) / 1e3;
        entryFinalEventProps.elapsedMinutes = elapsedSecs / 60;
        entryFinalEventProps.elapsedHours = elapsedSecs / 60 / 60;
        entryFinalEventProps.elapsedDays = elapsedSecs / 60 / 60 / 24;
      }
      for (const key in this.timeTo) {
        const propertyName = "timeTo" + key.charAt(0).toUpperCase() + key.slice(1);
        entryFinalEventProps[propertyName] = this.timeTo[key];
      }
      for (const [key, val] of Object.entries(referralChainLengths)) {
        if (val !== "(none)") {
          entryFinalEventProps[`chainLength${key}`] = val;
        }
      }
      const cdnEntryTimestamp = Number(getQueryParameters().cdn_ts);
      if (cdnEntryTimestamp) {
        entryFinalEventProps.elapsedSinceCDNEntry = globalTime.getNow() - cdnEntryTimestamp;
        removeQueryParameters(["cdn_ts"]);
      }
      internalAnalytics.pushEvent(
        "EntryFinal",
        entryFinalEventProps,
        internalAnalytics.toDefaultAndPixelAndAdServer
      );
      this.sendConversionEvents({
        immediateReferrerId: this.getReferrerID(),
        zeroGenerationReferrerId: entryData.zeroEntrySourcePlayerID,
        eventProperties: entryFinalEventProps
      });
      if (this._gameConfig.postbackManagerKey) {
        void this.fireMarketingPostback();
      }
      trackCDNPerformance();
    });
  }
  async fireMarketingPostback() {
    if (!this.entryData.$adNetworkName) {
      return;
    }
    const postbackManager = new PostbackManager(
      this._gameConfig.postbackManagerKey
    );
    let os;
    switch (this.osType) {
      case "ANDROID":
        os = "android";
        break;
      case "IOS":
        os = "ios";
        break;
      case "MOBILE_WEB":
        os = navigator.userAgent.match(/(iPod|iPhone|iPad)/) && navigator.userAgent.match(/AppleWebKit/) ? "ios" : "android";
        break;
      default:
        os = "android";
        break;
    }
    const meta = { os };
    if (this.countryName) {
      meta.country = getIsoCodeFromCountryName(this.countryName);
    }
    try {
      await postbackManager.sendEvent(
        PostbackManagerEvents.AppLaunch,
        this.entryData,
        meta
      );
    } catch (e) {
      trackMarketingPostbackFailure(e, {
        $adNetworkName: this.entryData.$adNetworkName
      });
    }
  }
  sendConversionEvents(opts) {
    if (!opts.immediateReferrerId && !opts.zeroGenerationReferrerId) {
      return;
    }
    const events2 = [
      // Send an EntryConversion event to referrer's event stream:
      {
        userID: opts.immediateReferrerId,
        eventName: "EntryConversion",
        eventProperties: opts.eventProperties
      },
      // Send an ZeroEntryConversion event to zero generation user's event stream:
      {
        userID: opts.zeroGenerationReferrerId,
        eventName: "ZeroEntryConversion",
        eventProperties: opts.eventProperties
      }
    ].filter(
      // Ignore events without a target user.
      ({ userID }) => !!userID
    );
    void this.sendRawAnalyticsEvent(events2).catch(() => void 0);
  }
  /**
   * Send raw analytics events through Amplitude's HTTP API. Useful for sending
   * events to another user's event stream.
   *
   * Also updates the receiver's [temporal user properties](https://docs.dev.gc-internal.net/gcinstant/replicant-extensions/#default-extensions) if entry timestamps getter is set with `setEntryTimestampsGetter`.
   *
   * @param event Analytics event sent as is without supplementing with contextual properties.
   * @throws In case of network error or non-2xx HTTP status code.
   */
  async sendRawAnalyticsEvent(event, opts) {
    const eventsArray = Array.isArray(event) ? event : [event];
    if (eventsArray.length === 0) {
      return;
    }
    if (!this._gameConfig.amplitudeKey) {
      if (this._gameConfig.isDevelopment) {
        console.log("rawAnalyticsEvent", eventsArray);
      }
      return;
    }
    const userIds = (opts == null ? void 0 : opts.temporalUserPropertyUserIds) || Array.from(new Set(eventsArray.map((e) => e.userID)));
    const temporalUserProps = await this.getTemporalUserProperties(userIds);
    const amplitudeEvents = eventsArray.map((e) => ({
      event_type: e.eventName,
      event_properties: e.eventProperties,
      user_id: e.userID,
      user_properties: { ...temporalUserProps[e.userID], ...e.userProperties }
    }));
    await retryWithTiming(async () => {
      const res = await fetch("https://api.amplitude.com/2/httpapi", {
        method: "POST",
        body: JSON.stringify({
          api_key: this._gameConfig.amplitudeKey,
          events: amplitudeEvents
        }),
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) {
        let responseBody;
        try {
          responseBody = await res.text();
        } catch {
        }
        throw Error(
          `Request failed with status code ${res.status}: ${responseBody}`
        );
      }
    }).catch(({ error, requestMs, requestTries }) => {
      trackSendRawAnalyticsEventFailure(error, {
        requestMs,
        requestTries
      });
      throw error;
    });
  }
  logEvent(eventName, valueToSum, parameters) {
    console.log(
      "FBInstant.logEvent()",
      "eventName:",
      eventName,
      "valueToSum:",
      valueToSum,
      "parameters:",
      parameters
    );
  }
  /** @returns entry channel, i.e., the channel through which player entered game. */
  getChannel() {
    if (this.entryContextTournament) {
      return "TOURNAMENT";
    }
    const entryPoint = this.entryPointName;
    let $channel = this.entryData.$channel;
    if (!$channel) {
      if (entryPoint === "ad") {
        $channel = "AD";
      } else if (entryPoint === "bot_cta" || entryPoint === "bot_menu") {
        $channel = "CHATBOT";
      } else if (entryPoint === "home_screen_shortcut") {
        $channel = "HOMESCREEN";
      } else if (entryPoint === "feed") {
        return "SHARE";
      } else if (entryPoint === "admin_message") {
        return "UPDATE";
      } else if (this.osType === "ANDROID_APP" || this.osType === "IOS_APP") {
        $channel = "HOMESCREEN";
      } else {
        $channel = "NONVIRAL";
      }
    }
    return $channel;
  }
  trackTimeTo(methodName) {
    this.timeTo[methodName] = Math.round(window.performance.now()) / 1e3;
  }
  throwIfNotInitialized() {
    if (!this._initialized) {
      throw createError(
        "INVALID_OPERATION",
        "Not initialized, game must resolve Platform.initializeAsync first"
      );
    }
  }
  throwIfNotGameStarted() {
    if (!this._gameStarted) {
      throw createError(
        "INVALID_OPERATION",
        "Game is not started, game must resolve Platform.startGameAsync first"
      );
    }
  }
  throwIfStartGameAsyncIsCalledMultipleTimes() {
    if (this.didCallStartGameAsync) {
      throw createError(
        "INVALID_OPERATION",
        `Game should not call 'startGameAsync' more than once`
      );
    }
    this.didCallStartGameAsync = true;
  }
  /**
   * If a platform implementation doesn't use `Platform.sendEntryFinalAnalytics`,
   * it should call this at the start of its `sendEntryFinalAnalytics`.
   */
  throwIfSendEntryFinalAnalyticsIsCalledMultipleTimes() {
    if (this.didCallSendEntryFinalAnalytics) {
      throw createError(
        "INVALID_OPERATION",
        `Game should not call 'sendEntryFinalAnalytics' more than once`
      );
    }
    this.didCallSendEntryFinalAnalytics = true;
  }
  throwIfStorageAdapterIsNotConfigured() {
    if (!this.storage.hasAdapter() && !this._gameConfig.isDevelopment) {
      throw createError(
        "INVALID_OPERATION",
        "Storage adapter must be configured before loading storage in production mode"
      );
    }
  }
  throwIfStorageIsNotLoaded() {
    if (this.storage.entry.count === 0) {
      throw createError(
        "INVALID_OPERATION",
        "Analytics storage must be loaded before operation"
      );
    }
  }
  /**
   * Create and initialize new native bridge using referral code flow.
   * Should be called before startGameAsync.
   */
  async createNativeBridge(baseURL, referralCode, secret, productIDs = []) {
    throw new Error("not implemented");
  }
  /**
   * Initialize an existing native bridge.
   * Should be called before startGameAsync.
   */
  async initializeNativeBridge(baseURL, bridgeSecret, productIds = []) {
    throw new Error("not implemented");
  }
  /**
   * Set a country code. A country name is derived from the code.
   * The country name is used for social events.
   *
   * @param countryCode A country code that consists of two upper-case letters.
   */
  setCountry(countryCode) {
    this.countryName = getCountryNameFromIsoCode(countryCode) || void 0;
  }
  /**
   * Set the eCPM values used for tracking ad revenue.
   *
   * @param eCPM eCPM values in USD as returned by Replicant.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/ads/
   */
  setECPM(eCPM) {
    this.eCPM = eCPM;
  }
  /** Set exchange rates to be used for revenue analytics. */
  setExchangeRates(exchangeRates) {
    this.convertCurrency = getConvertCurrency(exchangeRates);
  }
  /**
   * Set a source of external AB tests.
   *
   * AB test bucket assignments can be [viewed in analytics](https://docs.dev.gc-internal.net/gcinstant/ab-tests/#viewing-in-analytics),
   * and the AB tag is appended to the existing [AB tag](https://docs.dev.gc-internal.net/gcinstant/ab-tests/#ab-tag).
   *
   * Can be used to integrate [replicant AB tests](https://docs.dev.gc-internal.net/replicant/ab-tests).
   *
   * @param externalABTestsSource A source of external AB tests.
   */
  setExternalABTestsSource(externalABTestsSource) {
    this.externalABTests.setSource(externalABTestsSource);
  }
  /**
   * Set the current time of the clock used for analytics.
   *
   * @param now The current time as a millisecond timestamp.
   */
  setTime(now) {
    globalTime.setNow(now);
  }
  tournamentWithPayloadDecoder(tournament) {
    return {
      getContextID: () => tournament.getContextID().toString(),
      getEndTime: () => tournament.getEndTime(),
      getTitle: () => tournament.getTitle(),
      getPayload: async () => {
        const rawPayload = tournament.getPayload();
        if (!rawPayload) {
          return void 0;
        }
        let payloadObject;
        if (typeof rawPayload === "string") {
          try {
            payloadObject = JSON.parse(rawPayload);
          } catch (error) {
            trackPayloadReadFailure(error, {
              rawPayload,
              type: "parseFailed"
            });
            return void 0;
          }
        } else {
          payloadObject = rawPayload;
        }
        return this.dataCodec.decode(payloadObject);
      }
    };
  }
  /**
   * Returns true by default. Override the method in specific Platform to control incrementing entry count.
   */
  shouldIncreaseEntryCount() {
    return true;
  }
}
__publicField(Platform, "ERROR_NO_PROVISION");
Platform.ERROR_NO_PROVISION = "Game must provide an onProvisionProductAsync callback";
export {
  GAME_CONFIG_DEFAULTS,
  Platform,
  Platform as default,
  getLocalStorageData,
  getOrSetLocalStorageData
};
